import { RecoverStepAttributionSchema } from "@redotech/redo-billing-common/recover/recover-attribution-types";
import { TagSchema } from "@redotech/redo-common-model-tags-common/tag";
import { TimeUnit } from "@redotech/redo-common-model/time";
import { CheckoutAbTestMetricToOptimize } from "@redotech/redo-conversion-core-common/checkouts/checkout-ab-test-builder";
import {
  AddressPropertiesSegmentSchema,
  CartValueSegmentSchema,
  CheckoutProbabilitySegmentSchema,
  CompoundNarySegmentSchema,
  CrossBorderShipmentSegmentSchema,
  CustomerReturnRateSegmentSchema,
  CustomerTagsSegmentSchema,
  DatetimeRangeSegmentSchema,
  DeliveryDistanceMilesSegmentSchema,
  DeliveryLocationNarySegmentSchema,
  DeliveryLocationRulesSegmentSchema,
  DeliveryLocationSegmentSchema,
  HistoricalOrderValueSegmentSchema,
  ItemQuantitySegmentSchema,
  LineItemPropertiesSegmentSchema,
  OrderCountSegmentSchema,
  OriginLocationSegmentSchema,
  PoBoxShipmentSegmentSchema,
  ProductCollectionsSegmentSchema,
  ProductMetafieldsSegmentSchema,
  ProductSkuSegmentSchema,
  ProductTagsSegmentSchema,
  ProductVendorSegmentSchema,
  ShipmentWeightSegmentSchema,
  TimeOfDaySegmentSchema,
} from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import { ParcelConditionKind } from "@redotech/redo-conversion-core-common/checkouts/parcel/parcel-tree";
import { CheckoutOptimizationWidgetType } from "@redotech/redo-conversion-core-common/checkouts/widget";
import { postPurchaseSegmentSchema } from "@redotech/redo-conversion-core-common/post-purchase/segment-schemas";
import { checkoutOptimizationTreatmentStorefrontFeaturesSchema } from "@redotech/redo-conversion-core-common/split-slices";
import {
  EventProducer,
  IdentityResolver,
} from "@redotech/redo-customer-event-common/shopify-storefront-event-payload";
import {
  afterNowRelativeExactTimeframeSchema,
  beforeNowRelativeExactTimeframeSchema,
  todayTimeframeSchema,
} from "@redotech/redo-customer-segments-common/marketing/segments/segment-timeframe-zod-schema";
import { CustomerCharacteristicType } from "@redotech/redo-customer-segments-common/marketing/segments/segment-types";
import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import {
  numberComparisonInequalityOperators,
  numberComparisonOperators,
} from "@redotech/redo-flows-core-common/comparison-operators";
import {
  ListToListMatchType,
  StringToListMatchType,
} from "@redotech/redo-flows-core-common/condition";
import { OmsConditionType } from "@redotech/redo-flows-core-common/oms-condition-type";
import {
  StepType,
  supportedWaitForEventTypes,
} from "@redotech/redo-flows-core-schema-common/common";
import {
  checkoutContextOperators,
  CheckoutContextPredicateExpression as CheckoutContextPredicateExpressionType,
} from "@redotech/redo-flows-core-schema-common/expressions/checkout-context-predicate";
import { dateComparisonOperators } from "@redotech/redo-flows-core-schema-common/expressions/date-expression";
import { textMatchOperators } from "@redotech/redo-flows-core-schema-common/expressions/text-expression";
import { ServiceSchema } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { CarrierSchema } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { Hazmat } from "@redotech/redo-fulfillment-carrier-common/hazmat";
import { FulfillmentOrderAddressWithIdSchema } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { RedoShopifyDeliveryMethodType } from "@redotech/redo-fulfillment-group-common/fulfillment-delivery-method-type";
import {
  cheapestWithinDaysSortingCriteria,
  SortingCriteria,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { ShopifyFulfillmentHoldReason } from "@redotech/redo-fulfillment-group-common/shopify-fulfillment-schemas";
import { PageSize } from "@redotech/redo-fulfillment-operations-common/packing-slips/packing-slip-builder";
import { PageOrientation } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import { WeightSchema } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import {
  OutboundParcelSchema,
  ParcelSchema,
} from "@redotech/redo-fulfillment-parcel-common/parcel";
import {
  CanadaPostOption,
  SpecialRateEligibility,
} from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { Provider } from "@redotech/redo-order-common/provider";
import * as zExt from "@redotech/rpc/ext";
import { SECONDS_IN_A_DAY } from "@redotech/util/time";
import { XorOfUnions } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  compensationStepSchema,
  restockingStepSchema,
  returnFlowConditionStepSchema,
} from "./compensation-restocking-step-schemas";
import {
  LoyaltyLionIntegrationTriggerKey,
  RechargeIntegrationTriggerKey,
  YotpoIntegrationTriggerKey,
} from "./integration-triggers";
import { OmsTriggerKey } from "./oms-trigger-key";
import {
  AutoHoldType,
  OmsActionType,
  OmsInsuranceType,
  OmsSettableFields,
  RoundWeightActionStep,
  SetWeightActionStep,
  UpdateShopifyOrderField,
  WeightRoundingType,
} from "./oms/actions";
import {
  ruleV3ActionsStepSchema,
  ruleV3ConditionsStepSchema,
} from "./rules-v3/schemas";
import {
  ruleActionsStepSchema,
  ruleConditionsStepSchema,
} from "./rules/schemas";
import { RuleTriggerType } from "./rules/types";
import { SchemaType } from "./schemas/schemas";
import {
  ConditionDataSource,
  UpsellConditionSource,
  WaitTimeUnit,
} from "./steps";
import { trackingPageScheduledEventSchema } from "./tracking-page-scheduled-event";
import {
  categories,
  Category,
  CheckoutOptimizationTriggerKey,
  CheckoutParcelTriggerKey,
  CompensationFlowTriggerKey,
  CustomEventTriggerKey,
  EwTriggerKey,
  HsaTriggerKey,
  MarketingAgentCustomKey,
  MarketingTriggerKey,
  OrderTrackingTriggerKey,
  PostPurchaseTriggerKey,
  RestockingFlowTriggerKey,
  ReturnProcessingTriggerKey,
  ReturnTrackingTriggerKey,
  ReviewsTriggerKey,
  TnplTriggerKey,
  TrackingPageTriggerKey,
  TriggerKey,
  WithdrawalFlowTriggerKey,
} from "./triggers";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export type {
  CompensationStep,
  RestockingStep,
  ReturnFlowConditionStep,
} from "./compensation-restocking-step-schemas";

export const ORIGIN_RETURN_TRACKING_TEAM_ID = "68f7e6321b01371d0025d1c5";

export function waitStepSeconds(step: Partial<WaitStep>): number | undefined {
  if (step.numSeconds !== undefined) {
    return step.numSeconds;
  } else if (step.numDays !== undefined) {
    return step.numDays * SECONDS_IN_A_DAY;
  }
  return undefined;
}

const baseStepSchema = z.object({
  customTitle: z.string().optional(),
  type: createEnum(StepType),
  id: z.string(),
});

const baseOmsActionStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.OMS_ACTION),
  actionType: createEnum(OmsActionType),
  field: createEnum(OmsSettableFields),
  nextId: z.string(),
});

// Probably should just convert categories to an enum
const orderTrackingCategory: (typeof categories)[number] = "Order tracking";
const returnTrackingCategory: (typeof categories)[number] = "Return tracking";
const tnplCategory: (typeof categories)[number] = "Try first";
const marketingCategory: (typeof categories)[number] = "Marketing";
const reviewsCategory: (typeof categories)[number] = "Reviews";
const omsCategory: (typeof categories)[number] = "Oms";
const checkoutOptimizationCategory: (typeof categories)[number] =
  "Checkout personalization";
const checkoutPackageCategory: (typeof categories)[number] = "Checkout package";
const upsellCategory: (typeof categories)[number] = "Upsell";
const postPurchaseCategory: (typeof categories)[number] = "Post-purchase";
const trackingPageCategory: (typeof categories)[number] = "Tracking page";
const ewCategory: (typeof categories)[number] = "Extended warranty";
const hsaCategory: (typeof categories)[number] = "HSA";
const integrationCategory: (typeof categories)[number] = "Integration";
const customEventCategory: (typeof categories)[number] = "Custom Event";
const rulesCategory: (typeof categories)[number] = "Rules";
const returnProcessingCategory: (typeof categories)[number] =
  "Return processing";
const compensationCategory: (typeof categories)[number] = "Compensation";
const restockingCategory: (typeof categories)[number] = "Restocking";
const withdrawalCategory: (typeof categories)[number] = "Withdrawal";

const schemaBooleanExpressionSchema_ = z.lazy(() =>
  z.union([
    numberComparisonExpressionSchema,
    textMatchExpressionSchema,
    arrayPredicateExpressionSchema,
    booleanEvaluationExpressionSchema,
    checkoutContextPredicateExpressionSchema,
  ]),
);
export const schemaBooleanExpressionSchema: z.ZodType<SchemaBooleanExpressionType> =
  schemaBooleanExpressionSchema_;

export const actionStepTypes = [
  StepType.SEND_EMAIL,
  StepType.SEND_SMS,
  StepType.SEND_WEBHOOK,
  StepType.MANAGE_CUSTOMER_TAGS,
  StepType.MANAGE_STATIC_SEGMENT,
  StepType.RECOVER,
  StepType.CONCIERGE_BLOCK,
] as const satisfies StepType[];
export type ActionStepType = (typeof actionStepTypes)[number];

export const actionStepTypeToFriendlyName: Record<ActionStepType, string> = {
  [StepType.SEND_EMAIL]: "Send email",
  [StepType.SEND_SMS]: "Send SMS",
  [StepType.SEND_WEBHOOK]: "Send webhook",
  [StepType.MANAGE_CUSTOMER_TAGS]: "Manage customer tags",
  [StepType.MANAGE_STATIC_SEGMENT]: "Manage static segments",
  [StepType.RECOVER]: "Recover",
  [StepType.CONCIERGE_BLOCK]: "Concierge block",
};

const conditionExpressionSchema_ = z.intersection(
  z.object({ _id: zExt.objectId().optional() }),
  z.union([
    z.object({
      dataSource: z.literal(ConditionDataSource.TriggerData),
      schemaBooleanExpression: schemaBooleanExpressionSchema,
    }),
    z.object({
      dataSource: z.literal(ConditionDataSource.ExistingSegment),
      segmentIds: z.array(z.string()),
    }),
    z.object({
      dataSource: z.literal(ConditionDataSource.InlineSegment),
      inlineSegment: z.object({
        mode: createEnum(ConjunctionMode),
        conditions: z.array(z.any()), //TODO: Replace with queryConditionSchema (requires migration)
      }),
    }),
    z.object({ dataSource: z.literal(ConditionDataSource.OfferResponse) }),
  ]),
);
export const conditionExpressionSchema: z.ZodType<ConditionExpression> =
  conditionExpressionSchema_;
export type ConditionExpression = z.infer<typeof conditionExpressionSchema_>;

const skipConditionSchema_ = z.intersection(
  conditionExpressionSchema_,
  z.object({ actionTypes: z.array(createEnum(actionStepTypes)).nullish() }),
);
export const skipConditionSchema: z.ZodType<SkipCondition> =
  skipConditionSchema_;
export type SkipCondition = z.infer<typeof skipConditionSchema_>;

const baseTriggerStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.TRIGGER),
  schemaType: createEnum(SchemaType),
  category: createEnum(categories),
  nextId: z.string(),
  skipConditions: z
    .object({
      conjunctionMode: z.literal(ConjunctionMode.OR), // Currently only OR is supported, but I figure we may as well include the field for future proofing.
      conditions: z.array(skipConditionSchema_),
    })
    .optional(),
  shouldSkipSmartSending: z.boolean().nullish(),
});

export const flowCategoriesWithSkipConditions: Category[] = [
  "Marketing",
  "Order tracking",
  "Custom Event",
];

// Define specific trigger step schemas for each trigger type
const orderTrackingTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(orderTrackingCategory),
  key: createEnum(OrderTrackingTriggerKey),
});
const orderTrackingTriggerStepSchema: z.ZodType<OrderTrackingTriggerStep> =
  orderTrackingTriggerStepSchema_;
export interface OrderTrackingTriggerStep extends z.infer<
  typeof orderTrackingTriggerStepSchema_
> {}

const returnTrackingTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(returnTrackingCategory),
  key: createEnum(ReturnTrackingTriggerKey),
});
const returnTrackingTriggerStepSchema: z.ZodType<ReturnTrackingTriggerStep> =
  returnTrackingTriggerStepSchema_;
export interface ReturnTrackingTriggerStep extends z.infer<
  typeof returnTrackingTriggerStepSchema_
> {}

const returnProcessingTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(returnProcessingCategory),
  key: createEnum(ReturnProcessingTriggerKey),
});
const returnProcessingTriggerStepSchema: z.ZodType<ReturnProcessingTriggerStep> =
  returnProcessingTriggerStepSchema_;
export interface ReturnProcessingTriggerStep extends z.infer<
  typeof returnProcessingTriggerStepSchema_
> {}

const compensationFlowTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(compensationCategory),
  key: createEnum(CompensationFlowTriggerKey),
});
const compensationFlowTriggerStepSchema: z.ZodType<CompensationFlowTriggerStep> =
  compensationFlowTriggerStepSchema_;
export interface CompensationFlowTriggerStep extends z.infer<
  typeof compensationFlowTriggerStepSchema_
> {}

const restockingFlowTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(restockingCategory),
  key: createEnum(RestockingFlowTriggerKey),
});
export const restockingFlowTriggerStepSchema: z.ZodType<RestockingFlowTriggerStep> =
  restockingFlowTriggerStepSchema_;
export interface RestockingFlowTriggerStep extends z.infer<
  typeof restockingFlowTriggerStepSchema_
> {}

const withdrawalFlowTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(withdrawalCategory),
  key: createEnum(WithdrawalFlowTriggerKey),
});
const withdrawalFlowTriggerStepSchema: z.ZodType<WithdrawalFlowTriggerStep> =
  withdrawalFlowTriggerStepSchema_;
export interface WithdrawalFlowTriggerStep extends z.infer<
  typeof withdrawalFlowTriggerStepSchema_
> {}

const tnplTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(tnplCategory),
  key: createEnum(TnplTriggerKey),
});
const tnplTriggerStepSchema: z.ZodType<TnplTriggerStep> =
  tnplTriggerStepSchema_;
export interface TnplTriggerStep extends z.infer<
  typeof tnplTriggerStepSchema_
> {}

const baseMarketingTriggerStepSchema = baseTriggerStepSchema.extend({
  category: z.literal(marketingCategory),
});
// Break down marketing triggers into individual schemas
const emailSignupTriggerStepSchema_ = baseMarketingTriggerStepSchema.extend({
  key: z.literal(MarketingTriggerKey.EMAIL_SIGNUP),
});
const emailSignupTriggerStepSchema: z.ZodType<EmailSignupTriggerStep> =
  emailSignupTriggerStepSchema_;
export interface EmailSignupTriggerStep extends z.infer<
  typeof emailSignupTriggerStepSchema_
> {}

export const emailSignupShopifyTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.EMAIL_SIGNUP_SHOPIFY),
  });
const emailSignupShopifyTriggerStepSchema: z.ZodType<EmailSignupShopifyTriggerStep> =
  emailSignupShopifyTriggerStepSchema_;
export interface EmailSignupShopifyTriggerStep extends z.infer<
  typeof emailSignupShopifyTriggerStepSchema_
> {}

const smsSignupTriggerStepSchema_ = baseMarketingTriggerStepSchema.extend({
  key: z.literal(MarketingTriggerKey.SMS_SIGNUP),
});
// Backwards compatibility: normalize deprecated "sms_signup" key to "sms_confirmed"
const smsSignupTriggerStepSchema: z.ZodType<SmsSignupTriggerStep> =
  z.preprocess((val: any) => {
    if (!val) {
      return val;
    }
    // Convert Mongoose document to plain object if needed
    const plainObj = val.toObject ? val.toObject() : val;
    if (plainObj.key === "sms_signup") {
      plainObj.key = "sms_confirmed";
    }
    return plainObj;
  }, smsSignupTriggerStepSchema_) as z.ZodType<SmsSignupTriggerStep>;
// TODO: Switch to cleaner version in mid-December 2025 after outdated flows in temporal have all finished
// Replace above with:
// const smsSignupTriggerStepSchema: z.ZodType<SmsSignupTriggerStep> =
//   smsSignupTriggerStepSchema_;
export interface SmsSignupTriggerStep extends z.infer<
  typeof smsSignupTriggerStepSchema_
> {}

const marketingCampaignTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.MARKETING_CAMPAIGN),
  });
const marketingCampaignTriggerStepSchema: z.ZodType<MarketingCampaignTriggerStep> =
  marketingCampaignTriggerStepSchema_;
export interface MarketingCampaignTriggerStep extends z.infer<
  typeof marketingCampaignTriggerStepSchema_
> {}

const cartAbandonedTriggerStepSchema_ = baseMarketingTriggerStepSchema.extend({
  key: z.literal(MarketingTriggerKey.CART_ABANDONED),
});
const cartAbandonedTriggerStepSchema: z.ZodType<CartAbandonedTriggerStep> =
  cartAbandonedTriggerStepSchema_;
export interface CartAbandonedTriggerStep extends z.infer<
  typeof cartAbandonedTriggerStepSchema_
> {}

const browseAbandonedTriggerStepSchema_ = baseMarketingTriggerStepSchema.extend(
  { key: z.literal(MarketingTriggerKey.BROWSE_ABANDONED) },
);
const browseAbandonedTriggerStepSchema: z.ZodType<BrowseAbandonedTriggerStep> =
  browseAbandonedTriggerStepSchema_;
export interface BrowseAbandonedTriggerStep extends z.infer<
  typeof browseAbandonedTriggerStepSchema_
> {}

export const checkoutAbandonedTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.CHECKOUT_ABANDONED),
  });
const checkoutAbandonedTriggerStepSchema: z.ZodType<CheckoutAbandonedTriggerStep> =
  checkoutAbandonedTriggerStepSchema_;
export interface CheckoutAbandonedTriggerStep extends z.infer<
  typeof checkoutAbandonedTriggerStepSchema_
> {}

const bigcommerceCartAbandonedTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.BIGCOMMERCE_CART_ABANDONED),
  });
const bigcommerceCartAbandonedTriggerStepSchema: z.ZodType<BigcommerceCartAbandonedTriggerStep> =
  bigcommerceCartAbandonedTriggerStepSchema_;
export interface BigcommerceCartAbandonedTriggerStep extends z.infer<
  typeof bigcommerceCartAbandonedTriggerStepSchema_
> {}

const csCartAbandonedTriggerStepSchema_ = baseMarketingTriggerStepSchema.extend(
  { key: z.literal(MarketingTriggerKey.COMMENTSOLD_CART_ABANDONED) },
);
const csCartAbandonedTriggerStepSchema: z.ZodType<CsCartAbandonedTriggerStep> =
  csCartAbandonedTriggerStepSchema_;
export interface CsCartAbandonedTriggerStep extends z.infer<
  typeof csCartAbandonedTriggerStepSchema_
> {}

const csBrowseAbandonedTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.COMMENTSOLD_BROWSE_ABANDONED),
  });
const csBrowseAbandonedTriggerStepSchema: z.ZodType<CsBrowseAbandonedTriggerStep> =
  csBrowseAbandonedTriggerStepSchema_;
export interface CsBrowseAbandonedTriggerStep extends z.infer<
  typeof csBrowseAbandonedTriggerStepSchema_
> {}

const csCheckoutAbandonedTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.COMMENTSOLD_CHECKOUT_ABANDONED),
  });
const csCheckoutAbandonedTriggerStepSchema: z.ZodType<CsCheckoutAbandonedTriggerStep> =
  csCheckoutAbandonedTriggerStepSchema_;
export interface CsCheckoutAbandonedTriggerStep extends z.infer<
  typeof csCheckoutAbandonedTriggerStepSchema_
> {}

const bigcommerceCheckoutAbandonedTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.BIGCOMMERCE_CHECKOUT_ABANDONED),
  });
const bigcommerceCheckoutAbandonedTriggerStepSchema: z.ZodType<BigcommerceCheckoutAbandonedTriggerStep> =
  bigcommerceCheckoutAbandonedTriggerStepSchema_;
export interface BigcommerceCheckoutAbandonedTriggerStep extends z.infer<
  typeof bigcommerceCheckoutAbandonedTriggerStepSchema_
> {}

const backInStockTriggerStepSchema_ = baseMarketingTriggerStepSchema.extend({
  key: z.literal(MarketingTriggerKey.BACK_IN_STOCK),
  triggerSpecificFields: z
    .object({ minimumInventoryThreshold: z.number().int().min(0).default(0) })
    .nullish(),
});
export const backInStockTriggerStepSchema: z.ZodType<BackInStockTriggerStep> =
  backInStockTriggerStepSchema_;
export interface BackInStockTriggerStep extends z.infer<
  typeof backInStockTriggerStepSchema_
> {}

export const customerGroupEnteredTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.CUSTOMER_GROUP_ENTERED),
  });
const customerGroupEnteredTriggerStepSchema: z.ZodType<CustomerGroupEnteredTriggerStep> =
  customerGroupEnteredTriggerStepSchema_;
export interface CustomerGroupEnteredTriggerStep extends z.infer<
  typeof customerGroupEnteredTriggerStepSchema_
> {}

export const customerGroupExitedTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.CUSTOMER_GROUP_EXITED),
  });
const customerGroupExitedTriggerStepSchema: z.ZodType<CustomerGroupExitedTriggerStep> =
  customerGroupExitedTriggerStepSchema_;
export interface CustomerGroupExitedTriggerStep extends z.infer<
  typeof customerGroupExitedTriggerStepSchema_
> {}

const warrantyRegistrationTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.WARRANTY_REGISTRATION),
  });
export const warrantyRegistrationTriggerStepSchema: z.ZodType<WarrantyRegistrationTriggerStep> =
  warrantyRegistrationTriggerStepSchema_;
export interface WarrantyRegistrationTriggerStep extends z.infer<
  typeof warrantyRegistrationTriggerStepSchema_
> {}

const marketingLowInventoryTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.LOW_INVENTORY),
  });
const marketingLowInventoryTriggerStepSchema: z.ZodType<MarketingLowInventoryTriggerStep> =
  marketingLowInventoryTriggerStepSchema_;
export interface MarketingLowInventoryTriggerStep extends z.infer<
  typeof marketingLowInventoryTriggerStepSchema_
> {}

const marketingDateTriggerDimensions = [
  CustomerCharacteristicType.BIRTHDAY,
] as const;
export type MarketingDateTriggerDimension =
  (typeof marketingDateTriggerDimensions)[number];

const marketingDateTriggerStepSchema_ = baseMarketingTriggerStepSchema.extend({
  key: z.literal(MarketingTriggerKey.DATE),
  triggerSpecificFields: z.object({
    dimension: createEnum(CustomerCharacteristicType),
    comparison: z.union([
      beforeNowRelativeExactTimeframeSchema,
      afterNowRelativeExactTimeframeSchema,
      todayTimeframeSchema,
    ]),
  }),
});
const marketingDateTriggerStepSchema: z.ZodType<MarketingDateTriggerStep> =
  marketingDateTriggerStepSchema_;
export interface MarketingDateTriggerStep extends z.infer<
  typeof marketingDateTriggerStepSchema_
> {}

/** Days a balance must have been the holder's before we contact them. */
export const DEFAULT_GIFT_CARD_MINIMUM_DAYS_HELD = 30;

const marketingGiftCardBalanceTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.GIFT_CARD_BALANCE),
    triggerSpecificFields: z
      .object({
        minimumDaysHeld: z
          .number()
          .int()
          .min(0)
          .max(365)
          .default(DEFAULT_GIFT_CARD_MINIMUM_DAYS_HELD),
      })
      .nullish(),
  });
const marketingGiftCardBalanceTriggerStepSchema: z.ZodType<MarketingGiftCardBalanceTriggerStep> =
  marketingGiftCardBalanceTriggerStepSchema_;
export interface MarketingGiftCardBalanceTriggerStep extends z.infer<
  typeof marketingGiftCardBalanceTriggerStepSchema_
> {}

export enum PriceDropAmountType {
  PERCENTAGE = "percentage",
  CURRENCY = "currency",
}
const priceDropAmountTypeSchema = createEnum(PriceDropAmountType);

const priceDropContactRequirementsSchema = z.object({
  productViewed: z.boolean(),
  addedToCart: z.boolean(),
  startedCheckout: z.boolean(),
});
export type PriceDropContactRequirements = z.infer<
  typeof priceDropContactRequirementsSchema
>;

const marketingPriceDropTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.PRICE_DROP),
    triggerSpecificFields: z.object({
      minimumPriceDropAmount: z.object({
        type: priceDropAmountTypeSchema,
        value: z.number().positive(),
      }),
      contactRequirements: priceDropContactRequirementsSchema,
      daysBack: z.number().int().min(1).max(365).default(30),
    }),
  });
const marketingPriceDropTriggerStepSchema: z.ZodType<MarketingPriceDropTriggerStep> =
  marketingPriceDropTriggerStepSchema_;
export interface MarketingPriceDropTriggerStep extends z.infer<
  typeof marketingPriceDropTriggerStepSchema_
> {}

const productSubscriptionCancelledTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.PRODUCT_SUBSCRIPTION_CANCELLED),
  });
const productSubscriptionCancelledTriggerStepSchema: z.ZodType<ProductSubscriptionCancelledTriggerStep> =
  productSubscriptionCancelledTriggerStepSchema_;
export interface ProductSubscriptionCancelledTriggerStep extends z.infer<
  typeof productSubscriptionCancelledTriggerStepSchema_
> {}

const refundReturnSubmittedTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.REFUND_RETURN_SUBMITTED),
  });
const refundReturnSubmittedTriggerStepSchema: z.ZodType<RefundReturnSubmittedTriggerStep> =
  refundReturnSubmittedTriggerStepSchema_;
export interface RefundReturnSubmittedTriggerStep extends z.infer<
  typeof refundReturnSubmittedTriggerStepSchema_
> {}

const exchangeProcessedWithCreditTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.EXCHANGE_PROCESSED_WITH_CREDIT),
  });
const exchangeProcessedWithCreditTriggerStepSchema: z.ZodType<ExchangeProcessedWithCreditTriggerStep> =
  exchangeProcessedWithCreditTriggerStepSchema_;
export interface ExchangeProcessedWithCreditTriggerStep extends z.infer<
  typeof exchangeProcessedWithCreditTriggerStepSchema_
> {}

const inboundReceivedTriggerStepSchema_ = baseMarketingTriggerStepSchema.extend(
  { key: z.literal(MarketingTriggerKey.INBOUND_RECEIVED) },
);
const inboundReceivedTriggerStepSchema: z.ZodType<InboundReceivedTriggerStep> =
  inboundReceivedTriggerStepSchema_;
export interface InboundReceivedTriggerStep extends z.infer<
  typeof inboundReceivedTriggerStepSchema_
> {}

const marketingTriggerStepSchemas_ = z.union([
  emailSignupTriggerStepSchema,
  emailSignupShopifyTriggerStepSchema,
  smsSignupTriggerStepSchema,
  marketingCampaignTriggerStepSchema,
  cartAbandonedTriggerStepSchema,
  browseAbandonedTriggerStepSchema,
  checkoutAbandonedTriggerStepSchema,
  bigcommerceCartAbandonedTriggerStepSchema,
  bigcommerceCheckoutAbandonedTriggerStepSchema,
  csCartAbandonedTriggerStepSchema,
  csBrowseAbandonedTriggerStepSchema,
  csCheckoutAbandonedTriggerStepSchema,
  backInStockTriggerStepSchema,
  customerGroupEnteredTriggerStepSchema,
  customerGroupExitedTriggerStepSchema,
  warrantyRegistrationTriggerStepSchema,
  marketingLowInventoryTriggerStepSchema,
  marketingDateTriggerStepSchema,
  marketingGiftCardBalanceTriggerStepSchema,
  marketingPriceDropTriggerStepSchema,
  productSubscriptionCancelledTriggerStepSchema,
  refundReturnSubmittedTriggerStepSchema,
  exchangeProcessedWithCreditTriggerStepSchema,
  inboundReceivedTriggerStepSchema,
]);
export const marketingTriggerStepSchemas: z.ZodType<MarketingTriggerStep> =
  marketingTriggerStepSchemas_;
export type MarketingTriggerStep = z.infer<typeof marketingTriggerStepSchemas_>;

const reviewsTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(reviewsCategory),
  key: createEnum(ReviewsTriggerKey),
});
const reviewsTriggerStepSchema: z.ZodType<ReviewsTriggerStep> =
  reviewsTriggerStepSchema_;
export interface ReviewsTriggerStep extends z.infer<
  typeof reviewsTriggerStepSchema_
> {}

const hsaTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(hsaCategory),
  key: createEnum(HsaTriggerKey),
});
const hsaTriggerStepSchema: z.ZodType<HsaTriggerStep> = hsaTriggerStepSchema_;
export interface HsaTriggerStep extends z.infer<typeof hsaTriggerStepSchema_> {}

const omsTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(omsCategory),
  key: createEnum(OmsTriggerKey),
  triggerSpecificFields: z
    .object({
      triggerKind: z.string().optional(),
      triggerSource: z.string().optional(),
      triggerBomIds: z.array(z.string()).optional(),
      triggerBomTags: z.array(z.string()).optional(),
      batchMinSize: z.number().int().optional(),
      batchMaxSize: z.number().int().optional(),
      maxWorkOrderQuantity: z.number().int().optional(),
    })
    .passthrough()
    .optional(),
});
const omsTriggerStepSchema: z.ZodType<OmsTriggerStep> = omsTriggerStepSchema_;
export interface OmsTriggerStep extends z.infer<typeof omsTriggerStepSchema_> {}

const checkoutOptimizationTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(checkoutOptimizationCategory),
  key: createEnum(CheckoutOptimizationTriggerKey),
  // Unlike other trigger step types, the checkout optimization trigger is
  // allowed to have no next step — this represents a "no checkout tree"
  // state that `updateCheckoutTree` interprets as a request to unset the
  // active checkout tree entirely (see `isEmptyCheckoutTree`).
  nextId: z.string().optional(),
});
const checkoutOptimizationTriggerStepSchema: z.ZodType<CheckoutOptimizationTriggerStep> =
  checkoutOptimizationTriggerStepSchema_;
export interface CheckoutOptimizationTriggerStep extends z.infer<
  typeof checkoutOptimizationTriggerStepSchema_
> {}

const checkoutPackageTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(checkoutPackageCategory),
  key: createEnum(CheckoutParcelTriggerKey),
});
const checkoutPackageTriggerStepSchema: z.ZodType<CheckoutPackageTriggerStep> =
  checkoutPackageTriggerStepSchema_;
export interface CheckoutPackageTriggerStep extends z.infer<
  typeof checkoutPackageTriggerStepSchema_
> {}

const upsellTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(upsellCategory),
  key: z.literal(OrderTrackingTriggerKey.POST_PURCHASE_UPSELL),
});
const upsellTriggerStepSchema: z.ZodType<UpsellTriggerStep> =
  upsellTriggerStepSchema_;
export interface UpsellTriggerStep extends z.infer<
  typeof upsellTriggerStepSchema_
> {}

const postPurchaseTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(postPurchaseCategory),
  key: createEnum(PostPurchaseTriggerKey),
});
const postPurchaseTriggerStepSchema: z.ZodType<PostPurchaseTriggerStep> =
  postPurchaseTriggerStepSchema_;
export interface PostPurchaseTriggerStep extends z.infer<
  typeof postPurchaseTriggerStepSchema_
> {}

const trackingPageTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(trackingPageCategory),
  key: createEnum(TrackingPageTriggerKey),
});
const trackingPageTriggerStepSchema: z.ZodType<TrackingPageTriggerStep> =
  trackingPageTriggerStepSchema_;
export interface TrackingPageTriggerStep extends z.infer<
  typeof trackingPageTriggerStepSchema_
> {}

const extendedWarrantyTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(ewCategory),
  key: createEnum(EwTriggerKey),
});
const extendedWarrantyTriggerStepSchema: z.ZodType<ExtendedWarrantyTriggerStep> =
  extendedWarrantyTriggerStepSchema_;
export interface ExtendedWarrantyTriggerStep extends z.infer<
  typeof extendedWarrantyTriggerStepSchema_
> {}

const yotpoTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(integrationCategory),
  key: createEnum(YotpoIntegrationTriggerKey),
});
const yotpoTriggerStepSchema: z.ZodType<YotpoIntegrationTriggerStep> =
  yotpoTriggerStepSchema_;
export interface YotpoIntegrationTriggerStep extends z.infer<
  typeof yotpoTriggerStepSchema_
> {}

const rechargeTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(integrationCategory),
  key: createEnum(RechargeIntegrationTriggerKey),
});
const rechargeTriggerStepSchema: z.ZodType<RechargeIntegrationTriggerStep> =
  rechargeTriggerStepSchema_;
export interface RechargeIntegrationTriggerStep extends z.infer<
  typeof rechargeTriggerStepSchema_
> {}

const loyaltyLionTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(integrationCategory),
  key: createEnum(LoyaltyLionIntegrationTriggerKey),
});
const loyaltyLionTriggerStepSchema: z.ZodType<LoyaltyLionIntegrationTriggerStep> =
  loyaltyLionTriggerStepSchema_;
export interface LoyaltyLionIntegrationTriggerStep extends z.infer<
  typeof loyaltyLionTriggerStepSchema_
> {}

export enum CustomEventPropertyOperator {
  EQUALS = "equals",
  CONTAINS = "contains",
  GREATER_THAN = "greater_than",
  LESS_THAN = "less_than",
}

const customEventPropertyConditionSchema_ = z.object({
  property: z.string(),
  operator: createEnum(CustomEventPropertyOperator),
  value: z.union([z.string(), z.number(), z.boolean()]),
});
export const customEventPropertyConditionSchema: z.ZodType<CustomEventPropertyCondition> =
  customEventPropertyConditionSchema_;
export interface CustomEventPropertyCondition extends z.infer<
  typeof customEventPropertyConditionSchema_
> {}

const customEventTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(customEventCategory),
  key: z.literal(CustomEventTriggerKey.CUSTOM_EVENT),
  eventName: z.string(),
  triggerSpecificFields: z
    .object({
      conditions: z
        .array(customEventPropertyConditionSchema_)
        .nullish()
        .default([]),
    })
    .nullish(),
});
const customEventTriggerStepSchema: z.ZodType<CustomEventTriggerStep> =
  customEventTriggerStepSchema_;
export interface CustomEventTriggerStep extends z.infer<
  typeof customEventTriggerStepSchema_
> {}

const rulesTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(rulesCategory),
  key: createEnum(RuleTriggerType),
});
export const rulesTriggerStepSchema: z.ZodType<RulesTriggerStep> =
  rulesTriggerStepSchema_;
export interface RulesTriggerStep extends z.infer<
  typeof rulesTriggerStepSchema_
> {}

// TODO: Once zod 4 is released, switch to discriminated union
const triggerStepSchema_ = z.union([
  orderTrackingTriggerStepSchema,
  returnTrackingTriggerStepSchema,
  returnProcessingTriggerStepSchema,
  compensationFlowTriggerStepSchema,
  restockingFlowTriggerStepSchema,
  withdrawalFlowTriggerStepSchema,
  tnplTriggerStepSchema,
  marketingTriggerStepSchemas,
  reviewsTriggerStepSchema,
  hsaTriggerStepSchema,
  omsTriggerStepSchema,
  checkoutOptimizationTriggerStepSchema,
  checkoutPackageTriggerStepSchema,
  upsellTriggerStepSchema,
  postPurchaseTriggerStepSchema,
  trackingPageTriggerStepSchema,
  extendedWarrantyTriggerStepSchema,
  yotpoTriggerStepSchema,
  rechargeTriggerStepSchema,
  loyaltyLionTriggerStepSchema,
  customEventTriggerStepSchema,
  rulesTriggerStepSchema,
]);
export const triggerStepSchema: z.ZodType<TriggerStep> = triggerStepSchema_;
export type TriggerStep = z.infer<typeof triggerStepSchema_>;

const waitStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.WAIT),
  numDays: z.number(),
  numSeconds: z.number().optional(),
  timeUnit: createEnum(WaitTimeUnit).default(WaitTimeUnit.DAYS).optional(),
  nextId: z.string(),
});
export const waitStepSchema: z.ZodType<WaitStep> = waitStepSchema_;
export interface WaitStep extends z.infer<typeof waitStepSchema_> {}

const waitForEventFilterSchema_ = z
  .object({
    producer: z.array(createEnum(EventProducer)).optional(),
    emailIdentityResolver: z.array(createEnum(IdentityResolver)).optional(),
    phoneIdentityResolver: z.array(createEnum(IdentityResolver)).optional(),
  })
  .optional();
/**
 * Optional filter on a wait-for-event step. All specified allowlists must be
 * satisfied ("AND" across dimensions); within each dimension the event's
 * field must be one of the listed values ("OR" within dimension). An absent
 * or empty allowlist matches any value. Unset fields on the event are
 * resolved to default values by the evaluator (REDO for producer,
 * FIRST_PARTY for email/phone source).
 *
 * Used to let merchants filter on the provenance of a customer event — e.g.,
 * "only resume for first-party emails" or "only resume for events produced
 * by Contextual". See EventProducer and IdentityResolver enums in
 * redo-customer-event-common/shopify-storefront-event-payload.ts.
 */
export const waitForEventFilterSchema: z.ZodType<WaitForEventFilter> =
  waitForEventFilterSchema_;
export type WaitForEventFilter = z.infer<typeof waitForEventFilterSchema_>;

const waitForEventStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.WAIT_FOR_EVENT),
  eventType: createEnum(supportedWaitForEventTypes),
  nextId: z.string(),
  filter: waitForEventFilterSchema_,
});
const waitForEventStepSchema: z.ZodType<WaitForEventStep> =
  waitForEventStepSchema_;
export interface WaitForEventStep extends z.infer<
  typeof waitForEventStepSchema_
> {}

const sendEmailStepBaseSchema = baseStepSchema.extend({
  type: z.literal(StepType.SEND_EMAIL),
  emailAddressFieldName: z.string(),
  recipientNameFieldName: z.string(),
  nextId: z.string().optional(),
  disabled: z.boolean().optional(),
  splitId: zExt.objectId().optional(),
});

const sendEmailStepSchema_ = z.intersection(
  sendEmailStepBaseSchema,
  z.union([
    z.object({
      templateId: z.string(),
      originTemplateId: z.undefined().optional(),
    }),
    z.object({
      templateId: z.undefined().optional(),
      originTemplateId: z.string(),
    }),
  ]),
);

export const sendEmailStepSchema: z.ZodType<SendEmailStep> =
  sendEmailStepSchema_;
interface SendEmailTemplate extends z.infer<typeof sendEmailStepBaseSchema> {
  templateId: string;
  originTemplateId?: never;
}
interface SendEmailOriginTemplate extends z.infer<
  typeof sendEmailStepBaseSchema
> {
  originTemplateId: string;
  templateId?: never;
}

export type SendEmailStep = SendEmailTemplate | SendEmailOriginTemplate;

const sendSmsStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.SEND_SMS),
  // templateId can be either an ObjectId string or a special template identifier (e.g., "special-text-y-to-confirm-subscription")
  templateId: z.string(),
  phoneNumberFieldName: z.string(),
  recipientNameFieldName: z.string(),
  nextId: z.string().optional(),
  disabled: z.boolean().optional(),
  splitId: zExt.objectId().optional(),
});

export const sendSmsStepSchema: z.ZodType<SendSmsStep> = sendSmsStepSchema_;
export type SendSmsStep = z.infer<typeof sendSmsStepSchema_>;

export enum SendWebhookStepAuthType {
  NONE = "none",
  OAUTH2_CLIENT_CREDENTIALS = "oauth2_client_credentials",
}

const sendWebhookStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.SEND_WEBHOOK),
  destinationUrl: z.string(),
  headers: z.array(z.object({ key: z.string(), value: z.string() })),
  payload: z.string(),
  nextId: z.string().nullish(),
  disabled: z.boolean(),
  authType: createEnum(SendWebhookStepAuthType).nullish(),
  oauth2Config: z
    .object({
      tokenUrl: z.string(),
      clientId: z.string(),
      clientSecret: z.string(),
      audience: z.string().nullish(),
      scope: z.string().nullish(),
    })
    .nullish(),
});
const sendWebhookStepSchema: z.ZodType<SendWebhookStep> =
  sendWebhookStepSchema_;
export interface SendWebhookStep extends z.infer<
  typeof sendWebhookStepSchema_
> {}

// Concierge block — fires a Temporal workflow that asks Shopper AI to send
// a message. Prompt is passed through verbatim; the workflow enriches it with
// schema-instance context before handing it to the AI.
const conciergeBlockStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.CONCIERGE_BLOCK),
  prompt: z.string(),
  nextId: z.string().optional(),
  disabled: z.boolean().optional(),
  includeOrderTrackingLink: z.boolean().optional(),
});
const conciergeBlockStepSchema: z.ZodType<ConciergeBlockStep> =
  conciergeBlockStepSchema_;
export interface ConciergeBlockStep extends z.infer<
  typeof conciergeBlockStepSchema_
> {}

export type ActionStep =
  | SendEmailStep
  | SendSmsStep
  | SendWebhookStep
  | ManageCustomerTagsStep
  | ManageStaticSegmentStep
  | RecoverStep
  | ConciergeBlockStep;
export function isActionStep(step: FlowStep): step is ActionStep {
  return actionStepTypes.includes(step.type as ActionStep["type"]);
}

// Customer Tag Management Step
export enum CustomerTagOperation {
  ADD = "add",
  REMOVE = "remove",
}

const manageCustomerTagsStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.MANAGE_CUSTOMER_TAGS),
  operation: createEnum(CustomerTagOperation),
  tagIds: z.array(z.string()).min(1),
  nextId: z.string(),
  disabled: z.boolean(),
});
const manageCustomerTagsStepSchema: z.ZodType<ManageCustomerTagsStep> =
  manageCustomerTagsStepSchema_;
export interface ManageCustomerTagsStep extends z.infer<
  typeof manageCustomerTagsStepSchema_
> {}

// Static Segment Management Step
export enum CustomerSegmentOperation {
  ADD = "add",
  REMOVE = "remove",
}

const manageStaticSegmentStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.MANAGE_STATIC_SEGMENT),
  operation: createEnum(CustomerSegmentOperation),
  segmentId: z.string(),
  nextId: z.string(),
  disabled: z.boolean(),
});
const manageStaticSegmentStepSchema: z.ZodType<ManageStaticSegmentStep> =
  manageStaticSegmentStepSchema_;
export interface ManageStaticSegmentStep extends z.infer<
  typeof manageStaticSegmentStepSchema_
> {}

const recoverStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.RECOVER),
  templateId: z.string(),
  phoneNumberFieldName: z.string(),
  nextId: z.string(),
  disabled: z.boolean(),
  splitId: zExt.objectId().optional(),
  attribution: RecoverStepAttributionSchema.optional(),
});
const recoverStepSchema: z.ZodType<RecoverStep> = recoverStepSchema_;
export interface RecoverStep extends z.infer<typeof recoverStepSchema_> {}

const numberComparisonExpressionSchema_ = z.object({
  type: z.literal("number_comparison"),
  field: z.union([z.string(), z.number()]),
  operator: createEnum(numberComparisonOperators),
  comparisonValue: z.number(),
});
const numberComparisonExpressionSchema: z.ZodType<NumberComparisonExpression> =
  numberComparisonExpressionSchema_;
export interface NumberComparisonExpression extends z.infer<
  typeof numberComparisonExpressionSchema_
> {}

const textMatchExpressionSchema_ = z.object({
  type: z.literal("text_match"),
  field: z.union([z.string(), z.number()]),
  operator: createEnum(["startsWith", "endsWith", "includes", "equals"]),
  matchValues: z.array(z.string()),
});
const textMatchExpressionSchema: z.ZodType<TextMatchExpression> =
  textMatchExpressionSchema_;
export interface TextMatchExpression extends z.infer<
  typeof textMatchExpressionSchema_
> {}

export type SchemaBooleanExpressionType = z.infer<
  typeof schemaBooleanExpressionSchema_
>;

const arrayPredicateExpressionSchema_ = z.object({
  type: z.literal("array_predicate"),
  field: z.union([z.string(), z.number()]),
  operator: createEnum(["some", "every"]),
  get condition() {
    return schemaBooleanExpressionSchema;
  },
});
const arrayPredicateExpressionSchema: z.ZodType<ArrayPredicateExpression> =
  arrayPredicateExpressionSchema_;
export interface ArrayPredicateExpression extends z.infer<
  typeof arrayPredicateExpressionSchema_
> {}

const booleanEvaluationExpressionSchema_ = z.object({
  type: z.literal("boolean_evaluation"),
  field: z.union([z.string(), z.number()]),
  value: z.boolean().optional(),
});
const booleanEvaluationExpressionSchema: z.ZodType<BooleanEvaluationExpression> =
  booleanEvaluationExpressionSchema_;
export interface BooleanEvaluationExpression extends z.infer<
  typeof booleanEvaluationExpressionSchema_
> {}

// Product selection schema for product membership predicates
const productSelectionSchema = z.object({
  productId: z.string(),
  productTitle: z.string(),
  imageUrl: z.string().optional(),
  variantIds: z.array(z.string()).optional(),
  variantTitles: z.array(z.string()).optional(),
});

// Checkout context predicate schema - discriminated union based on predicateType
const checkoutContextPredicateExpressionSchema_ = z.discriminatedUnion(
  "predicateType",
  [
    z.object({
      type: z.literal("checkout_context_predicate"),
      field: z.union([z.string(), z.number()]),
      predicateType: z.literal("collection_membership"),
      operator: createEnum(checkoutContextOperators),
      collectionIds: z.array(z.string()),
    }),
    z.object({
      type: z.literal("checkout_context_predicate"),
      field: z.union([z.string(), z.number()]),
      predicateType: z.literal("product_membership"),
      operator: createEnum(checkoutContextOperators),
      products: z.array(productSelectionSchema),
    }),
    z.object({
      type: z.literal("checkout_context_predicate"),
      field: z.union([z.string(), z.number()]),
      predicateType: z.literal("product_tags"),
      operator: createEnum(checkoutContextOperators),
      tags: z.array(z.string()),
    }),
    z.object({
      type: z.literal("checkout_context_predicate"),
      field: z.union([z.string(), z.number()]),
      predicateType: z.literal("cart_product_count"),
      operator: createEnum(numberComparisonOperators),
      comparisonValue: z.number(),
    }),
    z.object({
      type: z.literal("checkout_context_predicate"),
      field: z.union([z.string(), z.number()]),
      predicateType: z.literal("cart_subtotal"),
      operator: createEnum(numberComparisonOperators),
      comparisonValue: z.number(),
    }),
    z.object({
      type: z.literal("checkout_context_predicate"),
      field: z.union([z.string(), z.number()]),
      predicateType: z.literal("discount_code"),
      operator: createEnum(textMatchOperators),
      matchValues: z.array(z.string()),
    }),
    z.object({
      type: z.literal("checkout_context_predicate"),
      field: z.union([z.string(), z.number()]),
      predicateType: z.literal("discount_percentage"),
      operator: createEnum(numberComparisonOperators),
      comparisonValue: z.number(),
    }),
    z.object({
      type: z.literal("checkout_context_predicate"),
      field: z.union([z.string(), z.number()]),
      predicateType: z.literal("cart_any_item_out_of_stock"),
    }),
    z.object({
      type: z.literal("checkout_context_predicate"),
      field: z.union([z.string(), z.number()]),
      predicateType: z.literal("recover_discount_eligibility"),
      recoverStepId: z.string(),
    }),
  ],
);
const checkoutContextPredicateExpressionSchema: z.ZodType<CheckoutContextPredicateExpressionType> =
  checkoutContextPredicateExpressionSchema_;

const conditionStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.CONDITION),
  expression: conditionExpressionSchema_,
  nextTrueId: z.string(),
  nextFalseId: z.string(),
  description: z.string().optional(),
});
export const conditionStepSchema: z.ZodType<ConditionStep> =
  conditionStepSchema_;
export interface ConditionStep extends z.infer<typeof conditionStepSchema_> {}

const upsellConditionStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.UPSELL_CONDITION),
  expression: z
    .object({
      source: createEnum(UpsellConditionSource),
      products: z
        .array(z.object({ id: z.string(), name: z.string() }))
        .optional(),
      collection: z.string().optional(),
    })
    .optional(),
  isDefault: z.boolean().optional(),
  nextTrueId: z.string().optional(),
  nextFalseId: z.string().optional(),
  name: z.string().optional(),
});
const upsellConditionStepSchema: z.ZodType<UpsellConditionStep> =
  upsellConditionStepSchema_;
export interface UpsellConditionStep extends z.infer<
  typeof upsellConditionStepSchema_
> {}

const upsellStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.UPSELL),
  name: z.string().optional(),
  upsellId: z.string(),
  nextId: z.string().optional(),
});
const upsellStepSchema: z.ZodType<UpsellStep> = upsellStepSchema_;
export interface UpsellStep extends z.infer<typeof upsellStepSchema_> {}

const downsellStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.DOWNSELL),
  name: z.string().optional(),
  downsellId: z.string(),
  nextId: z.string().optional(),
});
const downsellStepSchema: z.ZodType<DownsellStep> = downsellStepSchema_;
export interface DownsellStep extends z.infer<typeof downsellStepSchema_> {}

const doNothingStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.DO_NOTHING),
});
const doNothingStepSchema: z.ZodType<DoNothingStep> = doNothingStepSchema_;
export interface DoNothingStep extends z.infer<typeof doNothingStepSchema_> {}

/* Checkout personalization */
const rcoSplitStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.RCO_SPLIT),
  name: z.string(),
  hypothesis: z.string(),
  splitId: zExt.objectId(),
  metricToOptimize: createEnum(CheckoutAbTestMetricToOptimize),
  treatments: z.array(
    z.object({
      treatmentId: zExt.objectId(),
      nextId: z.string(),
      name: z.string(),
      weight: z.number(),
      storefrontFeatures:
        checkoutOptimizationTreatmentStorefrontFeaturesSchema.optional(),
    }),
  ),
  isActive: z.boolean(),
});
const rcoSplitStepSchema: z.ZodType<RcoSplitStep> = rcoSplitStepSchema_;
export interface RcoSplitStep extends z.infer<typeof rcoSplitStepSchema_> {}

const checkoutExperienceStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.CHECKOUT_EXPERIENCE),
  name: z.string(),
  widgets: z.array(createEnum(CheckoutOptimizationWidgetType)),
  checkoutExperienceId: zExt.objectId(),
  nextId: z.string().optional(), // Should never be set
});
const checkoutExperienceStepSchema: z.ZodType<CheckoutExperienceStep> =
  checkoutExperienceStepSchema_;
export interface CheckoutExperienceStep extends z.infer<
  typeof checkoutExperienceStepSchema_
> {}

const blockCheckoutStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.BLOCK_CHECKOUT),
  nextId: z.string().optional(), // Should never be set
});
const blockCheckoutStepSchema: z.ZodType<BlockCheckoutStep> =
  blockCheckoutStepSchema_;
export interface BlockCheckoutStep extends z.infer<
  typeof blockCheckoutStepSchema_
> {}

/* Segmentation */
export const rcoDeliveryDistanceMilesSegmentSchema =
  DeliveryDistanceMilesSegmentSchema;
export type RcoDeliveryDistanceMilesSegmentSchema = z.infer<
  typeof rcoDeliveryDistanceMilesSegmentSchema
>;
export const rcoOrderCountSegmentSchema = OrderCountSegmentSchema;
export type RcoOrderCountSegmentSchema = z.infer<
  typeof rcoOrderCountSegmentSchema
>;
export const rcoDeliveryLocationSegmentSchema = DeliveryLocationSegmentSchema;
export type RcoDeliveryLocationSegmentSchema = z.infer<
  typeof rcoDeliveryLocationSegmentSchema
>;

export const rcoDeliveryLocationRulesSegmentSchema =
  DeliveryLocationRulesSegmentSchema;
export type RcoDeliveryLocationRulesSegmentSchema = z.infer<
  typeof rcoDeliveryLocationRulesSegmentSchema
>;
export const rcoCustomerTagsSegmentSchema = CustomerTagsSegmentSchema;
export type RcoCustomerTagsSegmentSchema = z.infer<
  typeof rcoCustomerTagsSegmentSchema
>;
export const rcoProductTagsSegmentSchema = ProductTagsSegmentSchema;
export type RcoProductTagsSegmentSchema = z.infer<
  typeof rcoProductTagsSegmentSchema
>;
export const rcoProductCollectionsSegmentSchema =
  ProductCollectionsSegmentSchema;
export type RcoProductCollectionsSegmentSchema = z.infer<
  typeof rcoProductCollectionsSegmentSchema
>;
export const rcoProductVendorSegmentSchema = ProductVendorSegmentSchema;
export type RcoProductVendorSegmentSchema = z.infer<
  typeof rcoProductVendorSegmentSchema
>;
export const rcoProductSkuSegmentSchema = ProductSkuSegmentSchema;
export type RcoProductSkuSegmentSchema = z.infer<
  typeof rcoProductSkuSegmentSchema
>;
export const rcoItemQuantitySegmentSchema = ItemQuantitySegmentSchema;
export type RcoItemQuantitySegmentSchema = z.infer<
  typeof rcoItemQuantitySegmentSchema
>;
export const rcoOriginLocationSegmentSchema = OriginLocationSegmentSchema;
export type RcoOriginLocationSegmentSchema = z.infer<
  typeof rcoOriginLocationSegmentSchema
>;

export const rcoCustomerReturnRateSegmentSchema =
  CustomerReturnRateSegmentSchema;
export type RcoCustomerReturnRateSegmentSchema = z.infer<
  typeof rcoCustomerReturnRateSegmentSchema
>;

export const rcoTimeOfDaySegmentSchema = TimeOfDaySegmentSchema;
export type RcoTimeOfDaySegmentSchema = z.infer<
  typeof rcoTimeOfDaySegmentSchema
>;

export const rcoDatetimeRangeSegmentSchema = DatetimeRangeSegmentSchema;
export type RcoDatetimeRangeSegmentSchema = z.infer<
  typeof rcoDatetimeRangeSegmentSchema
>;

export const rcoCheckoutProbabilitySegmentSchema =
  CheckoutProbabilitySegmentSchema;
export type RcoCheckoutProbabilitySegmentSchema = z.infer<
  typeof rcoCheckoutProbabilitySegmentSchema
>;

export const rcoLineItemPropertiesSegmentSchema =
  LineItemPropertiesSegmentSchema;
export type RcoLineItemPropertiesSegmentSchema = z.infer<
  typeof rcoLineItemPropertiesSegmentSchema
>;
export const rcoProductMetafieldsSegmentSchema = ProductMetafieldsSegmentSchema;
export type RcoProductMetafieldsSegmentSchema = z.infer<
  typeof rcoProductMetafieldsSegmentSchema
>;
export const rcoCartValueSegmentSchema = CartValueSegmentSchema;
export const rcoShipmentWeightSegmentSchema = ShipmentWeightSegmentSchema;
export const rcoCrossBorderShipmentSegmentSchema =
  CrossBorderShipmentSegmentSchema;
export type RcoCrossBorderShipmentSegmentSchema = z.infer<
  typeof rcoCrossBorderShipmentSegmentSchema
>;
export const rcoPoBoxShipmentSegmentSchema = PoBoxShipmentSegmentSchema;
export type RcoPoBoxShipmentSegmentSchema = z.infer<
  typeof rcoPoBoxShipmentSegmentSchema
>;
export const rcoAddressPropertiesSegmentSchema = AddressPropertiesSegmentSchema;
export type RcoAddressPropertiesSegmentSchema = z.infer<
  typeof rcoAddressPropertiesSegmentSchema
>;
export const rcoHistoricalOrderValueSegmentSchema =
  HistoricalOrderValueSegmentSchema;
export type RcoHistoricalOrderValueSegmentSchema = z.infer<
  typeof rcoHistoricalOrderValueSegmentSchema
>;

const rcoSegmentSchema = z.union([
  rcoDeliveryDistanceMilesSegmentSchema,
  rcoOrderCountSegmentSchema,
  rcoDeliveryLocationSegmentSchema,
  rcoDeliveryLocationRulesSegmentSchema,
  rcoCustomerTagsSegmentSchema,
  rcoProductTagsSegmentSchema,
  rcoProductCollectionsSegmentSchema,
  rcoProductVendorSegmentSchema,
  rcoProductSkuSegmentSchema,
  rcoItemQuantitySegmentSchema,
  rcoOriginLocationSegmentSchema,
  rcoCustomerReturnRateSegmentSchema,
  rcoTimeOfDaySegmentSchema,
  rcoDatetimeRangeSegmentSchema,
  rcoCheckoutProbabilitySegmentSchema,
  rcoLineItemPropertiesSegmentSchema,
  rcoProductMetafieldsSegmentSchema,
  rcoCartValueSegmentSchema,
  rcoShipmentWeightSegmentSchema,
  rcoCrossBorderShipmentSegmentSchema,
  rcoPoBoxShipmentSegmentSchema,
  rcoAddressPropertiesSegmentSchema,
  rcoHistoricalOrderValueSegmentSchema,
]);
export type RcoSegmentSchema = z.infer<typeof rcoSegmentSchema>;

export const rcoNarySegmentSchema = z.union([
  DeliveryLocationNarySegmentSchema,
  CompoundNarySegmentSchema,
]);
export type RcoNarySegmentSchema = z.infer<typeof rcoNarySegmentSchema>;

const rcoNarySegmentEdgeSchema_ = z.object({
  edgeId: z.string(),
  nextId: z.string(),
});
export const rcoNarySegmentEdgeSchema: z.ZodType<RcoNarySegmentEdge> =
  rcoNarySegmentEdgeSchema_;
export interface RcoNarySegmentEdge extends z.infer<
  typeof rcoNarySegmentEdgeSchema_
> {}

export const rcoSegmentStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.RCO_SEGMENT),
  matchingNextId: z.string().optional(),
  nonMatchingNextId: z.string().optional(),
  edges: z.array(rcoNarySegmentEdgeSchema).optional(),
  defaultEdgeId: z.string().optional(),
  segment: z.union([rcoSegmentSchema, rcoNarySegmentSchema]),
});
export interface RcoSegmentStep extends z.infer<typeof rcoSegmentStepSchema> {}
/* End of segmentation */

/* Checkout parcels */
const rcoCheckoutPackageStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.RCO_CHECKOUT_PACKAGE),
  parcelId: zExt.objectId(),
  name: z.string(),
  dimensions: z.string(),
});
const rcoCheckoutPackageStepSchema: z.ZodType<RcoCheckoutPackageStep> =
  rcoCheckoutPackageStepSchema_;
export interface RcoCheckoutPackageStep extends z.infer<
  typeof rcoCheckoutPackageStepSchema_
> {}

const baseRcoCheckoutPackageConditionStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.RCO_CHECKOUT_PACKAGE_CONDITION),
  nextTrueId: z.string(),
  nextFalseId: z.string(),
});

const rcoCheckoutPackageWeightOzConditionStepSchema_ =
  baseRcoCheckoutPackageConditionStepSchema.extend({
    condition: z.object({
      kind: z.literal(ParcelConditionKind.WEIGHT_OZ),
      matchType: createEnum(numberComparisonInequalityOperators),
      matchValue: z.number(),
    }),
  });
const rcoCheckoutPackageWeightOzConditionStepSchema: z.ZodType<RcoCheckoutPackageWeightOzConditionStep> =
  rcoCheckoutPackageWeightOzConditionStepSchema_;
export interface RcoCheckoutPackageWeightOzConditionStep extends z.infer<
  typeof rcoCheckoutPackageWeightOzConditionStepSchema_
> {}

const rcoCheckoutPackageItemCountConditionStepSchema_ =
  baseRcoCheckoutPackageConditionStepSchema.extend({
    condition: z.object({
      kind: z.literal(ParcelConditionKind.ITEM_COUNT),
      matchType: createEnum(numberComparisonOperators),
      matchValue: z.number(),
    }),
  });
const rcoCheckoutPackageItemCountConditionStepSchema: z.ZodType<RcoCheckoutPackageItemCountConditionStep> =
  rcoCheckoutPackageItemCountConditionStepSchema_;
export interface RcoCheckoutPackageItemCountConditionStep extends z.infer<
  typeof rcoCheckoutPackageItemCountConditionStepSchema_
> {}

const rcoCheckoutPackageProductTagConditionStepSchema_ =
  baseRcoCheckoutPackageConditionStepSchema.extend({
    condition: z.object({
      kind: z.literal(ParcelConditionKind.PRODUCT_TAGS),
      matchType: createEnum(ListToListMatchType),
      matchValues: z.array(z.string()),
      requireExactMatch: z.boolean(),
    }),
  });
const rcoCheckoutPackageProductTagConditionStepSchema: z.ZodType<RcoCheckoutPackageProductTagConditionStep> =
  rcoCheckoutPackageProductTagConditionStepSchema_;
export interface RcoCheckoutPackageProductTagConditionStep extends z.infer<
  typeof rcoCheckoutPackageProductTagConditionStepSchema_
> {}

const rcoCheckoutPackageConditionStepSchema_ = z.union([
  rcoCheckoutPackageWeightOzConditionStepSchema,
  rcoCheckoutPackageItemCountConditionStepSchema,
  rcoCheckoutPackageProductTagConditionStepSchema,
]);
const rcoCheckoutPackageConditionStepSchema: z.ZodType<RcoCheckoutPackageConditionStep> =
  rcoCheckoutPackageConditionStepSchema_;
export type RcoCheckoutPackageConditionStep = z.infer<
  typeof rcoCheckoutPackageConditionStepSchema_
>;

/* End of checkout parcels */

/* End of checkout personalization */

/* Post-purchase flows */
const postPurchasePageStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.POST_PURCHASE_PAGE),
  name: z.string().nullable(),
  upsellPageId: zExt.objectId(),
  acceptNextId: z.string().optional(),
  declineNextId: z.string().optional(),
});
export const postPurchasePageStepSchema: z.ZodType<PostPurchasePageStep> =
  postPurchasePageStepSchema_;
export interface PostPurchasePageStep extends z.infer<
  typeof postPurchasePageStepSchema_
> {}

const postPurchaseSegmentStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.POST_PURCHASE_SEGMENT),
  matchingNextId: z.string().optional(),
  nonMatchingNextId: z.string().optional(),
  segment: postPurchaseSegmentSchema,
});
export const postPurchaseSegmentStepSchema: z.ZodType<PostPurchaseSegmentStep> =
  postPurchaseSegmentStepSchema_;
export interface PostPurchaseSegmentStep extends z.infer<
  typeof postPurchaseSegmentStepSchema_
> {}
/* End of post-purchase segmentation */

/* Post-purchase split testing */
const postPurchaseSplitStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.POST_PURCHASE_SPLIT),
  name: z.string(),
  description: z.string().optional(),
  splitId: zExt.objectId(),
  treatments: z.array(
    z.object({
      treatmentId: zExt.objectId(),
      nextId: z.string(),
      name: z.string(),
      weight: z.number(),
    }),
  ),
  isActive: z.boolean(),
});
export const postPurchaseSplitStepSchema: z.ZodType<PostPurchaseSplitStep> =
  postPurchaseSplitStepSchema_;
export interface PostPurchaseSplitStep extends z.infer<
  typeof postPurchaseSplitStepSchema_
> {}
/* End of post-purchase split testing */

/* End of post-purchase flows */

const setShippingServiceActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.SHIPPING_SERVICE),
  actionType: z.literal(OmsActionType.SET_SHIPPING_SERVICE),
  newShippingService: ServiceSchema,
  newShippingCarrier: CarrierSchema,
  carrierAccountId: z.string().nullish(),
});
const setShippingServiceActionStepSchema: z.ZodType<SetShippingServiceActionStep> =
  setShippingServiceActionStepSchema_;
export interface SetShippingServiceActionStep extends z.infer<
  typeof setShippingServiceActionStepSchema_
> {}

const setParcelActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.PARCEL),
  actionType: z.literal(OmsActionType.SET_PARCEL),
  parcels: z.array(OutboundParcelSchema).nullish(),
  /** @deprecated  use parcels instead */
  parcel: ParcelSchema.nullish(),
  addToExistingParcels: z.boolean().nullish(),
});
const setParcelActionStepSchema: z.ZodType<SetParcelActionStep> =
  setParcelActionStepSchema_;
export interface SetParcelActionStep extends z.infer<
  typeof setParcelActionStepSchema_
> {}

const setShippingServiceCheapestActionStepSchema_ =
  baseOmsActionStepSchema.extend({
    field: z.literal(OmsSettableFields.SHIPPING_SERVICE_CHEAPEST),
    actionType: z.literal(OmsActionType.SET_SHIPPING_SERVICE_CHEAPEST),
    servicesToChooseFrom: z.array(
      z.object({
        service: ServiceSchema,
        carrier: createEnum(Carriers),
        carrierAccountId: z.string().nullish(),
      }),
    ),
    servicesToPrioritize: z
      .array(
        z.object({
          service: ServiceSchema,
          carrier: createEnum(Carriers),
          carrierAccountId: z.string().nullish(),
        }),
      )
      .optional(),
    maxPriorityPriceDifference: z.number().optional(),
    selectionCriteria: z.literal(SortingCriteria.CHEAPEST),
  });
const setShippingServiceCheapestActionStepSchema: z.ZodType<SetShippingServiceCheapestActionStep> =
  setShippingServiceCheapestActionStepSchema_;
export interface SetShippingServiceCheapestActionStep extends z.infer<
  typeof setShippingServiceCheapestActionStepSchema_
> {}

const setShippingServiceFastestActionStepSchema_ =
  baseOmsActionStepSchema.extend({
    field: z.literal(OmsSettableFields.SHIPPING_SERVICE_FASTEST),
    actionType: z.literal(OmsActionType.SET_SHIPPING_SERVICE_FASTEST),
    servicesToChooseFrom: z.array(
      z.object({
        service: ServiceSchema,
        carrier: createEnum(Carriers),
        carrierAccountId: z.string().nullish(),
      }),
    ),
    servicesToPrioritize: z
      .array(
        z.object({
          service: ServiceSchema,
          carrier: createEnum(Carriers),
          carrierAccountId: z.string().nullish(),
        }),
      )
      .optional(),
    maxPriorityPriceDifference: z.number().optional(),
    selectionCriteria: z.literal(SortingCriteria.FASTEST),
  });
const setShippingServiceFastestActionStepSchema: z.ZodType<SetShippingServiceFastestActionStep> =
  setShippingServiceFastestActionStepSchema_;
export interface SetShippingServiceFastestActionStep extends z.infer<
  typeof setShippingServiceFastestActionStepSchema_
> {}

const setShippingServiceCheapestWithinDeliveryDaysActionStepSchema_ =
  baseOmsActionStepSchema.extend({
    field: z.literal(
      OmsSettableFields.SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS,
    ),
    actionType: z.literal(
      OmsActionType.SET_SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS,
    ),
    servicesToChooseFrom: z.array(
      z.object({
        service: ServiceSchema,
        carrier: createEnum(Carriers),
        carrierAccountId: z.string().nullish(),
      }),
    ),
    servicesToPrioritize: z
      .array(
        z.object({
          service: ServiceSchema,
          carrier: createEnum(Carriers),
          carrierAccountId: z.string().nullish(),
        }),
      )
      .optional(),
    maxPriorityPriceDifference: z.number().optional(),
    targetDeliveryDays: z.number(),
    selectionCriteria: createEnum(cheapestWithinDaysSortingCriteria),
  });
const setShippingServiceCheapestWithinDeliveryDaysActionStepSchema: z.ZodType<SetShippingServiceCheapestWithinDeliveryDaysActionStep> =
  setShippingServiceCheapestWithinDeliveryDaysActionStepSchema_;
export interface SetShippingServiceCheapestWithinDeliveryDaysActionStep extends z.infer<
  typeof setShippingServiceCheapestWithinDeliveryDaysActionStepSchema_
> {}

const setPackingSlipTemplateActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.PACKING_SLIP_TEMPLATE),
  actionType: z.literal(OmsActionType.SET_PACKING_SLIP_TEMPLATE),
  packingSlipName: z.string(),
  packingSlipTemplateId: z.string(),
});
const setPackingSlipTemplateActionStepSchema: z.ZodType<SetPackingSlipTemplateActionStep> =
  setPackingSlipTemplateActionStepSchema_;
export interface SetPackingSlipTemplateActionStep extends z.infer<
  typeof setPackingSlipTemplateActionStepSchema_
> {}

const setThirdPartyBillingAccountActionStepSchema_ =
  baseOmsActionStepSchema.extend({
    field: z.literal(OmsSettableFields.THIRD_PARTY_BILLING_ACCOUNT),
    actionType: z.literal(OmsActionType.SET_THIRD_PARTY_BILLING_ACCOUNT),
    thirdPartyBillingAccountId: z.string(),
    thirdPartyBillingAccountName: z.string(),
  });
const setThirdPartyBillingAccountActionStepSchema: z.ZodType<SetThirdPartyBillingAccountActionStep> =
  setThirdPartyBillingAccountActionStepSchema_;
export interface SetThirdPartyBillingAccountActionStep extends z.infer<
  typeof setThirdPartyBillingAccountActionStepSchema_
> {}

const setDeliveryMethodActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.DELIVERY_METHOD),
  actionType: z.literal(OmsActionType.SET_DELIVERY_METHOD),
  deliveryMethod: createEnum(RedoShopifyDeliveryMethodType),
});
const setDeliveryMethodActionStepSchema: z.ZodType<SetDeliveryMethodActionStep> =
  setDeliveryMethodActionStepSchema_;
export interface SetDeliveryMethodActionStep extends z.infer<
  typeof setDeliveryMethodActionStepSchema_
> {}

const setOverrideUseOriginalDestinationAddressActionStepSchema_ =
  baseOmsActionStepSchema.extend({
    field: z.literal(
      OmsSettableFields.OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS,
    ),
    actionType: z.literal(
      OmsActionType.SET_OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS,
    ),
    overrideUseOriginalDestinationAddress: z.boolean(),
  });
const setOverrideUseOriginalDestinationAddressActionStepSchema: z.ZodType<SetOverrideUseOriginalDestinationAddressActionStep> =
  setOverrideUseOriginalDestinationAddressActionStepSchema_;
export interface SetOverrideUseOriginalDestinationAddressActionStep extends z.infer<
  typeof setOverrideUseOriginalDestinationAddressActionStepSchema_
> {}

const generatePersonalizedSlipActionStepSchema_ =
  baseOmsActionStepSchema.extend({
    field: z.literal(OmsSettableFields.PERSONALIZED_SLIP),
    actionType: z.literal(OmsActionType.GENERATE_PERSONALIZED_SLIP),
    personalizedSlipTemplateId: z.string(),
    personalizedSlipTemplateName: z.string(),
    pageSizeOverride: createEnum(PageSize, { enumName: "PageSize" }).optional(),
    pageOrientationOverride: createEnum(PageOrientation, {
      enumName: "PageOrientation",
    }).optional(),
  });
const generatePersonalizedSlipActionStepSchema: z.ZodType<GeneratePersonalizedSlipActionStep> =
  generatePersonalizedSlipActionStepSchema_;
export interface GeneratePersonalizedSlipActionStep extends z.infer<
  typeof generatePersonalizedSlipActionStepSchema_
> {}

const setInternalNoteActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.INTERNAL_NOTE),
  actionType: z.literal(OmsActionType.SET_INTERNAL_NOTE),
  note: z.string(),
});
const setInternalNoteActionStepSchema: z.ZodType<SetInternalNoteActionStep> =
  setInternalNoteActionStepSchema_;
export interface SetInternalNoteActionStep extends z.infer<
  typeof setInternalNoteActionStepSchema_
> {}

const splitIntoParcelsActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.SPLIT_INTO_PARCELS),
  actionType: z.literal(OmsActionType.SPLIT_INTO_PARCELS),
});
const splitIntoParcelsActionStepSchema: z.ZodType<SplitIntoParcelsActionStep> =
  splitIntoParcelsActionStepSchema_;
export interface SplitIntoParcelsActionStep extends z.infer<
  typeof splitIntoParcelsActionStepSchema_
> {}

const addTagActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.TAG),
  actionType: z.literal(OmsActionType.ADD_TAG),
  tags: z.array(TagSchema),
});
const addTagActionStepSchema: z.ZodType<AddTagActionStep> =
  addTagActionStepSchema_;
export interface AddTagActionStep extends z.infer<
  typeof addTagActionStepSchema_
> {}

const addOrderTagsActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.ORDER_TAG),
  actionType: z.literal(OmsActionType.ADD_ORDER_TAGS),
  tags: z.array(z.string()),
});
const addOrderTagsActionStepSchema: z.ZodType<AddOrderTagsActionStep> =
  addOrderTagsActionStepSchema_;
export interface AddOrderTagsActionStep extends z.infer<
  typeof addOrderTagsActionStepSchema_
> {}

const placeHoldActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.PLACE_HOLD),
  actionType: z.literal(OmsActionType.PLACE_HOLD),
  holdOptions: z.object({
    holdType: createEnum([AutoHoldType.Duration, AutoHoldType.Indefinite]),
    duration: z.number().optional(),
    timeUnit: createEnum(TimeUnit).optional(),
    holdReason: createEnum(ShopifyFulfillmentHoldReason),
    reasonNotes: z.string().nullish(),
  }),
});
const placeHoldActionStepSchema: z.ZodType<PlaceHoldActionStep> =
  placeHoldActionStepSchema_;
export interface PlaceHoldActionStep extends z.infer<
  typeof placeHoldActionStepSchema_
> {}

const placeHoldUntilActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.PLACE_HOLD_UNTIL),
  actionType: z.literal(OmsActionType.PLACE_HOLD_UNTIL),
  holdOptions: z.object({
    holdType: z.literal(AutoHoldType.Date),
    date: z.coerce.date(),
    holdReason: createEnum(ShopifyFulfillmentHoldReason),
    reasonNotes: z.string().nullish(),
  }),
});
const placeHoldUntilActionStepSchema: z.ZodType<PlaceHoldUntilActionStep> =
  placeHoldUntilActionStepSchema_;
export interface PlaceHoldUntilActionStep extends z.infer<
  typeof placeHoldUntilActionStepSchema_
> {}

const setWeightActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.SET_WEIGHT),
  actionType: z.literal(OmsActionType.SET_WEIGHT),
  weight: WeightSchema,
});
const setWeightActionStepSchema: z.ZodType<SetWeightActionStep> =
  setWeightActionStepSchema_;

const roundWeightActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.ROUND_WEIGHT),
  actionType: z.literal(OmsActionType.ROUND_WEIGHT),
  roundingType: createEnum(WeightRoundingType),
  minimumWeight: WeightSchema,
});
const roundWeightActionStepSchema: z.ZodType<RoundWeightActionStep> =
  roundWeightActionStepSchema_;

const setAssignedUserActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.ASSIGNED_USER),
  actionType: z.literal(OmsActionType.SET_ASSIGNED_USER),
  userId: z.string().optional(),
  roundRobin: z.boolean().optional(),
  roundRobinUserIds: z.array(z.string()).optional(),
});
const setAssignedUserActionStepSchema: z.ZodType<SetAssignedUserActionStep> =
  setAssignedUserActionStepSchema_;
export interface SetAssignedUserActionStep extends z.infer<
  typeof setAssignedUserActionStepSchema_
> {}

const setDutiesPayerActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.DUTIES_PAYER),
  actionType: z.literal(OmsActionType.SET_DUTIES_PAYER),
  ddp: z.boolean(),
});
const setDutiesPayerActionStepSchema: z.ZodType<SetDutiesPayerActionStep> =
  setDutiesPayerActionStepSchema_;
export interface SetDutiesPayerActionStep extends z.infer<
  typeof setDutiesPayerActionStepSchema_
> {}

const setTaxIdentifiersActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.TAX_IDENTIFIERS),
  actionType: z.literal(OmsActionType.SET_TAX_IDENTIFIERS),
  taxIdentifierIds: z.array(z.string()),
});
const setTaxIdentifiersActionStepSchema: z.ZodType<SetTaxIdentifiersActionStep> =
  setTaxIdentifiersActionStepSchema_;
export interface SetTaxIdentifiersActionStep extends z.infer<
  typeof setTaxIdentifiersActionStepSchema_
> {}

const setReturnAddressActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.RETURN_ADDRESS),
  actionType: z.literal(OmsActionType.SET_RETURN_ADDRESS),
  address: FulfillmentOrderAddressWithIdSchema,
});
const setReturnAddressActionStepSchema: z.ZodType<SetReturnAddressActionStep> =
  setReturnAddressActionStepSchema_;
export interface SetReturnAddressActionStep extends z.infer<
  typeof setReturnAddressActionStepSchema_
> {}

const setOriginLocationActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.ORIGIN_LOCATION),
  actionType: z.literal(OmsActionType.SET_ORIGIN_LOCATION),
  locationId: z.string(),
});
const setOriginLocationActionStepSchema: z.ZodType<SetOriginLocationActionStep> =
  setOriginLocationActionStepSchema_;
export interface SetOriginLocationActionStep extends z.infer<
  typeof setOriginLocationActionStepSchema_
> {}

const setBuyReturnLabelActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.BUY_RETURN_LABEL),
  actionType: z.literal(OmsActionType.SET_BUY_RETURN_LABEL),
  buyReturnLabel: z.boolean(),
});
const setBuyReturnLabelActionStepSchema: z.ZodType<SetBuyReturnLabelActionStep> =
  setBuyReturnLabelActionStepSchema_;
export interface SetBuyReturnLabelActionStep extends z.infer<
  typeof setBuyReturnLabelActionStepSchema_
> {}

const setSendLabelPurchasedNotificationActionStepSchema_ =
  baseOmsActionStepSchema.extend({
    field: z.literal(OmsSettableFields.SEND_LABEL_PURCHASED_NOTIFICATION),
    actionType: z.literal(OmsActionType.SET_SEND_LABEL_PURCHASED_NOTIFICATION),
    sendLabelPurchasedNotification: z.boolean(),
  });
const setSendLabelPurchasedNotificationActionStepSchema: z.ZodType<SetSendLabelPurchasedNotificationActionStep> =
  setSendLabelPurchasedNotificationActionStepSchema_;
export interface SetSendLabelPurchasedNotificationActionStep extends z.infer<
  typeof setSendLabelPurchasedNotificationActionStepSchema_
> {}

const delayMarketplaceFulfillmentActionStepSchema_ =
  baseOmsActionStepSchema.extend({
    field: z.literal(OmsSettableFields.DELAY_MARKETPLACE_FULFILLMENT),
    actionType: z.literal(OmsActionType.DELAY_MARKETPLACE_FULFILLMENT),
    delayDays: z.number().int().min(1),
  });
const delayMarketplaceFulfillmentActionStepSchema: z.ZodType<DelayMarketplaceFulfillmentActionStep> =
  delayMarketplaceFulfillmentActionStepSchema_;
export interface DelayMarketplaceFulfillmentActionStep extends z.infer<
  typeof delayMarketplaceFulfillmentActionStepSchema_
> {}

const doNotNotifyMarketplaceActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.DO_NOT_NOTIFY_MARKETPLACE),
  actionType: z.literal(OmsActionType.DO_NOT_NOTIFY_MARKETPLACE),
});
const doNotNotifyMarketplaceActionStepSchema: z.ZodType<DoNotNotifyMarketplaceActionStep> =
  doNotNotifyMarketplaceActionStepSchema_;
export interface DoNotNotifyMarketplaceActionStep extends z.infer<
  typeof doNotNotifyMarketplaceActionStepSchema_
> {}

const updateShopifyOrderActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.UPDATE_SHOPIFY_ORDER),
  actionType: z.literal(OmsActionType.UPDATE_SHOPIFY_ORDER),
  updateField: createEnum(UpdateShopifyOrderField),
});
const updateShopifyOrderActionStepSchema: z.ZodType<UpdateShopifyOrderActionStep> =
  updateShopifyOrderActionStepSchema_;
export interface UpdateShopifyOrderActionStep extends z.infer<
  typeof updateShopifyOrderActionStepSchema_
> {}

const assignFulfillmentServiceActionStepSchema_ =
  baseOmsActionStepSchema.extend({
    field: z.literal(OmsSettableFields.FULFILLMENT_SERVICE),
    actionType: z.literal(OmsActionType.ASSIGN_FULFILLMENT_SERVICE),
    fulfillmentServiceId: z.string(),
    fulfillmentServiceName: z.string(),
  });
const assignFulfillmentServiceActionStepSchema: z.ZodType<AssignFulfillmentServiceActionStep> =
  assignFulfillmentServiceActionStepSchema_;
export interface AssignFulfillmentServiceActionStep extends z.infer<
  typeof assignFulfillmentServiceActionStepSchema_
> {}

const shippingOptionsSchema_ = z.object({
  signatureRequired: z.boolean().nullish(),
  adultSignatureRequired: z.boolean().nullish(),
  includeInsurance: z.boolean().nullish(),
  insuranceType: createEnum(OmsInsuranceType).nullish(),
  insurancePercentage: z.number().nullish(),
  insuranceFixedAmount: z.number().nullish(),
  overrideResidentialAddress: z.boolean().nullish(),
  specialRateEligibility: createEnum(SpecialRateEligibility).nullish(),
  hazmat: createEnum(Hazmat).nullish(),
  dryIceWeight: WeightSchema.nullish(),
  canadaPostOption: createEnum(CanadaPostOption, {
    enumName: "CanadaPostOption",
  }).nullish(),
});
const shippingOptionsSchema: z.ZodType<ShippingOptions> =
  shippingOptionsSchema_;
export interface ShippingOptions extends z.infer<
  typeof shippingOptionsSchema_
> {}

const setShippingOptionsActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.SHIPPING_OPTIONS),
  actionType: z.literal(OmsActionType.SET_SHIPPING_OPTIONS),
  shippingOptions: shippingOptionsSchema,
});
const setShippingOptionsActionStepSchema: z.ZodType<SetShippingOptionsActionStep> =
  setShippingOptionsActionStepSchema_;
export interface SetShippingOptionsActionStep extends z.infer<
  typeof setShippingOptionsActionStepSchema_
> {}

const baseOmsConditionStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.OMS_CONDITION),
  conditionType: createEnum(OmsConditionType),
  nextTrueId: z.string(),
  nextFalseId: z.string(),
});

const baseOmsConditionStepSchemaWithWhere = baseOmsConditionStepSchema.extend({
  where: z
    .array(
      baseOmsConditionStepSchema.omit({
        id: true,
        type: true,
        nextTrueId: true,
        nextFalseId: true,
      }),
    )
    .optional(),
});

const omsOrderTagConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.ORDER_TAG),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });
const omsOrderTagConditionStepSchema: z.ZodType<OmsOrderTagConditionStep> =
  omsOrderTagConditionStepSchema_;
export interface OmsOrderTagConditionStep extends z.infer<
  typeof omsOrderTagConditionStepSchema_
> {}

const omsSelectedShippingConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.SELECTED_SHIPPING),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });
const omsSelectedShippingConditionStepSchema: z.ZodType<OmsSelectedShippingConditionStep> =
  omsSelectedShippingConditionStepSchema_;
export interface OmsSelectedShippingConditionStep extends z.infer<
  typeof omsSelectedShippingConditionStepSchema_
> {}

const baseOmsSkuConditionStepSchema =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.SKU),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });

const baseOmsVendorConditionStepSchema =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.VENDOR),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });

const baseOmsProductNameConditionStepSchema =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_NAME),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });

const baseOmsVariantTitleConditionStepSchema =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.VARIANT_TITLE),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });

const baseOmsProductTypeConditionStepSchema =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_TYPE),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });

const baseOmsItemQuantityConditionStepSchema =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.ITEM_QUANTITY),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  });

const omsPackageWeightOzConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PACKAGE_WEIGHT_OZ),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  });
const omsPackageWeightOzConditionStepSchema: z.ZodType<OmsPackageWeightOzConditionStep> =
  omsPackageWeightOzConditionStepSchema_;
export interface OmsPackageWeightOzConditionStep extends z.infer<
  typeof omsPackageWeightOzConditionStepSchema_
> {}

const omsTotalValueConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.TOTAL_VALUE),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  });
const omsTotalValueConditionStepSchema: z.ZodType<OmsTotalValueConditionStep> =
  omsTotalValueConditionStepSchema_;
export interface OmsTotalValueConditionStep extends z.infer<
  typeof omsTotalValueConditionStepSchema_
> {}

const omsTaxesPaidConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.TAXES_PAID),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  });
const omsTaxesPaidConditionStepSchema: z.ZodType<OmsTaxesPaidConditionStep> =
  omsTaxesPaidConditionStepSchema_;
export interface OmsTaxesPaidConditionStep extends z.infer<
  typeof omsTaxesPaidConditionStepSchema_
> {}

const omsDestinationCountryConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.DESTINATION_COUNTRY),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsDestinationCountryConditionStepSchema: z.ZodType<OmsDestinationCountryConditionStep> =
  omsDestinationCountryConditionStepSchema_;
export interface OmsDestinationCountryConditionStep extends z.infer<
  typeof omsDestinationCountryConditionStepSchema_
> {}

const omsDestinationProvinceConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.DESTINATION_PROVINCE),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsDestinationProvinceConditionStepSchema: z.ZodType<OmsDestinationProvinceConditionStep> =
  omsDestinationProvinceConditionStepSchema_;
export interface OmsDestinationProvinceConditionStep extends z.infer<
  typeof omsDestinationProvinceConditionStepSchema_
> {}

const omsDeliveryMethodConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.DELIVERY_METHOD),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(createEnum(RedoShopifyDeliveryMethodType)),
  });
const omsDeliveryMethodConditionStepSchema: z.ZodType<OmsDeliveryMethodConditionStep> =
  omsDeliveryMethodConditionStepSchema_;
export interface OmsDeliveryMethodConditionStep extends z.infer<
  typeof omsDeliveryMethodConditionStepSchema_
> {}

const omsStreetAddressConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.STREET_ADDRESS),
    matchType: createEnum(textMatchOperators),
    matchValue: z.string(),
  });
const omsStreetAddressConditionStepSchema: z.ZodType<OmsStreetAddressConditionStep> =
  omsStreetAddressConditionStepSchema_;
export interface OmsStreetAddressConditionStep extends z.infer<
  typeof omsStreetAddressConditionStepSchema_
> {}

const omsStreetAddressListInputConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.STREET_ADDRESS_LIST_INPUT),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsStreetAddressListInputConditionStepSchema: z.ZodType<OmsStreetAddressListInputConditionStep> =
  omsStreetAddressListInputConditionStepSchema_;
export interface OmsStreetAddressListInputConditionStep extends z.infer<
  typeof omsStreetAddressListInputConditionStepSchema_
> {}

const omsSalesChannelConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.SALES_CHANNEL),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsSalesChannelConditionStepSchema: z.ZodType<OmsSalesChannelConditionStep> =
  omsSalesChannelConditionStepSchema_;
export interface OmsSalesChannelConditionStep extends z.infer<
  typeof omsSalesChannelConditionStepSchema_
> {}

const baseOmsProductTagConditionStepSchema =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_TAG),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });

const omsProductWhereClauseSchema_ = z.union([
  baseOmsProductNameConditionStepSchema.omit({
    id: true,
    type: true,
    nextTrueId: true,
    nextFalseId: true,
  }),
  baseOmsVariantTitleConditionStepSchema.omit({
    id: true,
    type: true,
    nextTrueId: true,
    nextFalseId: true,
  }),
  baseOmsProductTypeConditionStepSchema.omit({
    id: true,
    type: true,
    nextTrueId: true,
    nextFalseId: true,
  }),
  baseOmsProductTagConditionStepSchema.omit({
    id: true,
    type: true,
    nextTrueId: true,
    nextFalseId: true,
  }),
  baseOmsItemQuantityConditionStepSchema.omit({
    id: true,
    type: true,
    nextTrueId: true,
    nextFalseId: true,
  }),
  baseOmsSkuConditionStepSchema.omit({
    id: true,
    type: true,
    nextTrueId: true,
    nextFalseId: true,
  }),
  baseOmsVendorConditionStepSchema.omit({
    id: true,
    type: true,
    nextTrueId: true,
    nextFalseId: true,
  }),
]);
type OmsProductWhereClause = z.infer<typeof omsProductWhereClauseSchema_>;
const omsProductWhereClauseSchema: z.ZodType<OmsProductWhereClause> =
  omsProductWhereClauseSchema_;

const omsProductNameConditionStepSchema_ =
  baseOmsProductNameConditionStepSchema.extend({
    where: z.array(omsProductWhereClauseSchema).optional(),
  });
const omsProductNameConditionStepSchema: z.ZodType<OmsProductNameConditionStep> =
  omsProductNameConditionStepSchema_;
export interface OmsProductNameConditionStep extends z.infer<
  typeof omsProductNameConditionStepSchema_
> {}

const omsVariantTitleConditionStepSchema_ =
  baseOmsVariantTitleConditionStepSchema.extend({
    where: z.array(omsProductWhereClauseSchema).optional(),
  });
const omsVariantTitleConditionStepSchema: z.ZodType<OmsVariantTitleConditionStep> =
  omsVariantTitleConditionStepSchema_;
export interface OmsVariantTitleConditionStep extends z.infer<
  typeof omsVariantTitleConditionStepSchema_
> {}

const omsSkuConditionStepSchema_ = baseOmsSkuConditionStepSchema.extend({
  where: z.array(omsProductWhereClauseSchema).optional(),
});
const omsSkuConditionStepSchema: z.ZodType<OmsSkuConditionStep> =
  omsSkuConditionStepSchema_;
export interface OmsSkuConditionStep extends z.infer<
  typeof omsSkuConditionStepSchema_
> {}

const omsVendorConditionStepSchema_ = baseOmsVendorConditionStepSchema.extend({
  where: z.array(omsProductWhereClauseSchema).optional(),
});
const omsVendorConditionStepSchema: z.ZodType<OmsVendorConditionStep> =
  omsVendorConditionStepSchema_;
export interface OmsVendorConditionStep extends z.infer<
  typeof omsVendorConditionStepSchema_
> {}

const omsProductTypeConditionStepSchema_ =
  baseOmsProductTypeConditionStepSchema.extend({
    where: z.array(omsProductWhereClauseSchema).optional(),
  });
const omsProductTypeConditionStepSchema: z.ZodType<OmsProductTypeConditionStep> =
  omsProductTypeConditionStepSchema_;
export interface OmsProductTypeConditionStep extends z.infer<
  typeof omsProductTypeConditionStepSchema_
> {}

const omsUspsZoneConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.USPS_ZONE),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsUspsZoneConditionStepSchema: z.ZodType<OmsUspsZoneConditionStep> =
  omsUspsZoneConditionStepSchema_;
export interface OmsUspsZoneConditionStep extends z.infer<
  typeof omsUspsZoneConditionStepSchema_
> {}

const omsLineItemCustomAttributeConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.LINE_ITEM_CUSTOM_ATTRIBUTE),
    matchKey: z.string().default(""),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()).default([]),
  });
const omsLineItemCustomAttributeConditionStepSchema: z.ZodType<OmsLineItemCustomAttributeConditionStep> =
  omsLineItemCustomAttributeConditionStepSchema_;
export interface OmsLineItemCustomAttributeConditionStep extends z.infer<
  typeof omsLineItemCustomAttributeConditionStepSchema_
> {}

const omsAddressVerifiedConditionStepSchema_ =
  baseOmsConditionStepSchema.extend({
    conditionType: z.literal(OmsConditionType.ADDRESS_VERIFIED),
    matchValue: z.boolean().default(true),
  });
const omsAddressVerifiedConditionStepSchema: z.ZodType<OmsAddressVerifiedConditionStep> =
  omsAddressVerifiedConditionStepSchema_;
export interface OmsAddressVerifiedConditionStep extends z.infer<
  typeof omsAddressVerifiedConditionStepSchema_
> {}

const omsProductTagConditionStepSchema_ =
  baseOmsProductTagConditionStepSchema.extend({
    where: z.array(omsProductWhereClauseSchema).optional(),
  });
const omsProductTagConditionStepSchema: z.ZodType<OmsProductTagConditionStep> =
  omsProductTagConditionStepSchema_;
export interface OmsProductTagConditionStep extends z.infer<
  typeof omsProductTagConditionStepSchema_
> {}

const omsItemQuantityConditionStepSchema_ =
  baseOmsItemQuantityConditionStepSchema.extend({
    where: z.array(omsProductWhereClauseSchema).optional(),
  });
const omsItemQuantityConditionStepSchema: z.ZodType<OmsItemQuantityConditionStep> =
  omsItemQuantityConditionStepSchema_;
export interface OmsItemQuantityConditionStep extends z.infer<
  typeof omsItemQuantityConditionStepSchema_
> {}

const omsFulfillmentLocationConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.FULFILLMENT_LOCATION),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().nullish(),
  });
const omsFulfillmentLocationConditionStepSchema: z.ZodType<OmsFulfillmentLocationConditionStep> =
  omsFulfillmentLocationConditionStepSchema_;
export interface OmsFulfillmentLocationConditionStep extends z.infer<
  typeof omsFulfillmentLocationConditionStepSchema_
> {}

const omsOriginStoreConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.ORIGIN_STORE),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsOriginStoreConditionStepSchema: z.ZodType<OmsOriginStoreConditionStep> =
  omsOriginStoreConditionStepSchema_;
export interface OmsOriginStoreConditionStep extends z.infer<
  typeof omsOriginStoreConditionStepSchema_
> {}

const omsOrderNotesConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.ORDER_NOTES),
    matchType: createEnum(textMatchOperators),
    matchValue: z.string(),
  });
const omsOrderNotesConditionStepSchema: z.ZodType<OmsOrderNotesConditionStep> =
  omsOrderNotesConditionStepSchema_;
export interface OmsOrderNotesConditionStep extends z.infer<
  typeof omsOrderNotesConditionStepSchema_
> {}

const omsMarketplaceInternalNotesConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.MARKETPLACE_INTERNAL_NOTES),
    matchType: createEnum(textMatchOperators),
    matchValue: z.string(),
  });
const omsMarketplaceInternalNotesConditionStepSchema: z.ZodType<OmsMarketplaceInternalNotesConditionStep> =
  omsMarketplaceInternalNotesConditionStepSchema_;
export interface OmsMarketplaceInternalNotesConditionStep extends z.infer<
  typeof omsMarketplaceInternalNotesConditionStepSchema_
> {}

const omsPackageIsLearnedConditionStepSchema_ =
  baseOmsConditionStepSchema.extend({
    conditionType: z.literal(OmsConditionType.PACKAGE_IS_LEARNED),
    matchValue: z.boolean().default(true),
  });
const omsPackageIsLearnedConditionStepSchema: z.ZodType<OmsPackageIsLearnedConditionStep> =
  omsPackageIsLearnedConditionStepSchema_;
export interface OmsPackageIsLearnedConditionStep extends z.infer<
  typeof omsPackageIsLearnedConditionStepSchema_
> {}

const omsDestinationForecastTemperatureConditionStepSchema_ =
  baseOmsConditionStepSchema.extend({
    conditionType: z.literal(OmsConditionType.DESTINATION_FORECAST_TEMPERATURE),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
    defaultDeliveryDays: z.number().int().min(0).optional(),
  });
const omsDestinationForecastTemperatureConditionStepSchema: z.ZodType<OmsDestinationForecastTemperatureConditionStep> =
  omsDestinationForecastTemperatureConditionStepSchema_;
export interface OmsDestinationForecastTemperatureConditionStep extends z.infer<
  typeof omsDestinationForecastTemperatureConditionStepSchema_
> {}

export const omsActionStepSchema = z.union([
  setShippingServiceActionStepSchema,
  setParcelActionStepSchema,
  setShippingServiceCheapestActionStepSchema,
  setShippingServiceFastestActionStepSchema,
  setShippingServiceCheapestWithinDeliveryDaysActionStepSchema,
  setPackingSlipTemplateActionStepSchema,
  setThirdPartyBillingAccountActionStepSchema,
  addTagActionStepSchema,
  setShippingOptionsActionStepSchema,
  placeHoldActionStepSchema,
  placeHoldUntilActionStepSchema,
  setWeightActionStepSchema,
  roundWeightActionStepSchema,
  setAssignedUserActionStepSchema,
  setDutiesPayerActionStepSchema,
  setTaxIdentifiersActionStepSchema,
  setReturnAddressActionStepSchema,
  setOriginLocationActionStepSchema,
  setBuyReturnLabelActionStepSchema,
  assignFulfillmentServiceActionStepSchema,
  setDeliveryMethodActionStepSchema,
  setOverrideUseOriginalDestinationAddressActionStepSchema,
  generatePersonalizedSlipActionStepSchema,
  setInternalNoteActionStepSchema,
  splitIntoParcelsActionStepSchema,
  addOrderTagsActionStepSchema,
  setSendLabelPurchasedNotificationActionStepSchema,
  delayMarketplaceFulfillmentActionStepSchema,
  doNotNotifyMarketplaceActionStepSchema,
  updateShopifyOrderActionStepSchema,
]);
export type OmsActionStep = z.infer<typeof omsActionStepSchema>;

const omsFraudRiskConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.FRAUD_RISK),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().nullish(),
  });
const omsFraudRiskConditionStepSchema: z.ZodType<OmsFraudRiskConditionStep> =
  omsFraudRiskConditionStepSchema_;
export interface OmsFraudRiskConditionStep extends z.infer<
  typeof omsFraudRiskConditionStepSchema_
> {}

const omsAvailableInventoryConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.AVAILABLE_INVENTORY),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  });
const omsAvailableInventoryConditionStepSchema: z.ZodType<OmsAvailableInventoryConditionStep> =
  omsAvailableInventoryConditionStepSchema_;
export interface OmsAvailableInventoryConditionStep extends z.infer<
  typeof omsAvailableInventoryConditionStepSchema_
> {}

const omsMarketplaceConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.MARKETPLACE),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(createEnum(Provider)),
  });
const omsMarketplaceConditionStepSchema: z.ZodType<OmsMarketplaceConditionStep> =
  omsMarketplaceConditionStepSchema_;
export interface OmsMarketplaceConditionStep extends z.infer<
  typeof omsMarketplaceConditionStepSchema_
> {}

const omsShipmentTagConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.SHIPMENT_TAG),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });

const omsShipmentTagConditionStepSchema: z.ZodType<OmsShipmentTagConditionStep> =
  omsShipmentTagConditionStepSchema_;

export interface OmsShipmentTagConditionStep extends z.infer<
  typeof omsShipmentTagConditionStepSchema_
> {}

const omsCustomString1ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_STRING_1),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsCustomString1ConditionStepSchema: z.ZodType<OmsCustomString1ConditionStep> =
  omsCustomString1ConditionStepSchema_;
export interface OmsCustomString1ConditionStep extends z.infer<
  typeof omsCustomString1ConditionStepSchema_
> {}

const omsCustomString2ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_STRING_2),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsCustomString2ConditionStepSchema: z.ZodType<OmsCustomString2ConditionStep> =
  omsCustomString2ConditionStepSchema_;
export interface OmsCustomString2ConditionStep extends z.infer<
  typeof omsCustomString2ConditionStepSchema_
> {}

const omsCustomString3ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_STRING_3),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsCustomString3ConditionStepSchema: z.ZodType<OmsCustomString3ConditionStep> =
  omsCustomString3ConditionStepSchema_;
export interface OmsCustomString3ConditionStep extends z.infer<
  typeof omsCustomString3ConditionStepSchema_
> {}

const omsCustomNumber1ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_NUMBER_1),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  });
const omsCustomNumber1ConditionStepSchema: z.ZodType<OmsCustomNumber1ConditionStep> =
  omsCustomNumber1ConditionStepSchema_;
export interface OmsCustomNumber1ConditionStep extends z.infer<
  typeof omsCustomNumber1ConditionStepSchema_
> {}

const omsCustomNumber2ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_NUMBER_2),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  });
const omsCustomNumber2ConditionStepSchema: z.ZodType<OmsCustomNumber2ConditionStep> =
  omsCustomNumber2ConditionStepSchema_;
export interface OmsCustomNumber2ConditionStep extends z.infer<
  typeof omsCustomNumber2ConditionStepSchema_
> {}

const omsCustomNumber3ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_NUMBER_3),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  });
const omsCustomNumber3ConditionStepSchema: z.ZodType<OmsCustomNumber3ConditionStep> =
  omsCustomNumber3ConditionStepSchema_;
export interface OmsCustomNumber3ConditionStep extends z.infer<
  typeof omsCustomNumber3ConditionStepSchema_
> {}

const omsCustomDate1ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_DATE_1),
    matchType: createEnum(dateComparisonOperators),
    matchDate: z.date(),
  });
const omsCustomDate1ConditionStepSchema: z.ZodType<OmsCustomDate1ConditionStep> =
  omsCustomDate1ConditionStepSchema_;
export interface OmsCustomDate1ConditionStep extends z.infer<
  typeof omsCustomDate1ConditionStepSchema_
> {}

const omsCustomDate2ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_DATE_2),
    matchType: createEnum(dateComparisonOperators),
    matchDate: z.date(),
  });
const omsCustomDate2ConditionStepSchema: z.ZodType<OmsCustomDate2ConditionStep> =
  omsCustomDate2ConditionStepSchema_;
export interface OmsCustomDate2ConditionStep extends z.infer<
  typeof omsCustomDate2ConditionStepSchema_
> {}

const omsCustomDate3ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_DATE_3),
    matchType: createEnum(dateComparisonOperators),
    matchDate: z.date(),
  });
const omsCustomDate3ConditionStepSchema: z.ZodType<OmsCustomDate3ConditionStep> =
  omsCustomDate3ConditionStepSchema_;
export interface OmsCustomDate3ConditionStep extends z.infer<
  typeof omsCustomDate3ConditionStepSchema_
> {}

const omsProductCustomString1ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_CUSTOM_STRING_1),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsProductCustomString1ConditionStepSchema: z.ZodType<OmsProductCustomString1ConditionStep> =
  omsProductCustomString1ConditionStepSchema_;
export interface OmsProductCustomString1ConditionStep extends z.infer<
  typeof omsProductCustomString1ConditionStepSchema_
> {}

const omsProductCustomString2ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_CUSTOM_STRING_2),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsProductCustomString2ConditionStepSchema: z.ZodType<OmsProductCustomString2ConditionStep> =
  omsProductCustomString2ConditionStepSchema_;
export interface OmsProductCustomString2ConditionStep extends z.infer<
  typeof omsProductCustomString2ConditionStepSchema_
> {}

const omsProductCustomString3ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_CUSTOM_STRING_3),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsProductCustomString3ConditionStepSchema: z.ZodType<OmsProductCustomString3ConditionStep> =
  omsProductCustomString3ConditionStepSchema_;
export interface OmsProductCustomString3ConditionStep extends z.infer<
  typeof omsProductCustomString3ConditionStepSchema_
> {}

const omsProductCustomNumber1ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_CUSTOM_NUMBER_1),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  });
const omsProductCustomNumber1ConditionStepSchema: z.ZodType<OmsProductCustomNumber1ConditionStep> =
  omsProductCustomNumber1ConditionStepSchema_;
export interface OmsProductCustomNumber1ConditionStep extends z.infer<
  typeof omsProductCustomNumber1ConditionStepSchema_
> {}

const omsProductCustomNumber2ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_CUSTOM_NUMBER_2),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  });
const omsProductCustomNumber2ConditionStepSchema: z.ZodType<OmsProductCustomNumber2ConditionStep> =
  omsProductCustomNumber2ConditionStepSchema_;
export interface OmsProductCustomNumber2ConditionStep extends z.infer<
  typeof omsProductCustomNumber2ConditionStepSchema_
> {}

const omsProductCustomNumber3ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_CUSTOM_NUMBER_3),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  });
const omsProductCustomNumber3ConditionStepSchema: z.ZodType<OmsProductCustomNumber3ConditionStep> =
  omsProductCustomNumber3ConditionStepSchema_;
export interface OmsProductCustomNumber3ConditionStep extends z.infer<
  typeof omsProductCustomNumber3ConditionStepSchema_
> {}

const omsProductCustomDate1ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_CUSTOM_DATE_1),
    matchType: createEnum(dateComparisonOperators),
    matchDate: z.date(),
  });
const omsProductCustomDate1ConditionStepSchema: z.ZodType<OmsProductCustomDate1ConditionStep> =
  omsProductCustomDate1ConditionStepSchema_;
export interface OmsProductCustomDate1ConditionStep extends z.infer<
  typeof omsProductCustomDate1ConditionStepSchema_
> {}

const omsProductCustomDate2ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_CUSTOM_DATE_2),
    matchType: createEnum(dateComparisonOperators),
    matchDate: z.date(),
  });
const omsProductCustomDate2ConditionStepSchema: z.ZodType<OmsProductCustomDate2ConditionStep> =
  omsProductCustomDate2ConditionStepSchema_;
export interface OmsProductCustomDate2ConditionStep extends z.infer<
  typeof omsProductCustomDate2ConditionStepSchema_
> {}

const omsProductCustomDate3ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_CUSTOM_DATE_3),
    matchType: createEnum(dateComparisonOperators),
    matchDate: z.date(),
  });
const omsProductCustomDate3ConditionStepSchema: z.ZodType<OmsProductCustomDate3ConditionStep> =
  omsProductCustomDate3ConditionStepSchema_;
export interface OmsProductCustomDate3ConditionStep extends z.infer<
  typeof omsProductCustomDate3ConditionStepSchema_
> {}

const omsCustomAttributeString1ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsCustomAttributeString1ConditionStepSchema: z.ZodType<OmsCustomAttributeString1ConditionStep> =
  omsCustomAttributeString1ConditionStepSchema_;
export interface OmsCustomAttributeString1ConditionStep extends z.infer<
  typeof omsCustomAttributeString1ConditionStepSchema_
> {}

const omsCustomAttributeString2ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsCustomAttributeString2ConditionStepSchema: z.ZodType<OmsCustomAttributeString2ConditionStep> =
  omsCustomAttributeString2ConditionStepSchema_;
export interface OmsCustomAttributeString2ConditionStep extends z.infer<
  typeof omsCustomAttributeString2ConditionStepSchema_
> {}

const omsCustomAttributeString3ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsCustomAttributeString3ConditionStepSchema: z.ZodType<OmsCustomAttributeString3ConditionStep> =
  omsCustomAttributeString3ConditionStepSchema_;
export interface OmsCustomAttributeString3ConditionStep extends z.infer<
  typeof omsCustomAttributeString3ConditionStepSchema_
> {}

const omsCustomAttributeNumber1ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_1),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  });
const omsCustomAttributeNumber1ConditionStepSchema: z.ZodType<OmsCustomAttributeNumber1ConditionStep> =
  omsCustomAttributeNumber1ConditionStepSchema_;
export interface OmsCustomAttributeNumber1ConditionStep extends z.infer<
  typeof omsCustomAttributeNumber1ConditionStepSchema_
> {}

const omsCustomAttributeNumber2ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_2),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  });
const omsCustomAttributeNumber2ConditionStepSchema: z.ZodType<OmsCustomAttributeNumber2ConditionStep> =
  omsCustomAttributeNumber2ConditionStepSchema_;
export interface OmsCustomAttributeNumber2ConditionStep extends z.infer<
  typeof omsCustomAttributeNumber2ConditionStepSchema_
> {}

const omsCustomAttributeNumber3ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_3),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  });
const omsCustomAttributeNumber3ConditionStepSchema: z.ZodType<OmsCustomAttributeNumber3ConditionStep> =
  omsCustomAttributeNumber3ConditionStepSchema_;
export interface OmsCustomAttributeNumber3ConditionStep extends z.infer<
  typeof omsCustomAttributeNumber3ConditionStepSchema_
> {}

const omsCustomAttributeDate1ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_DATE_1),
    matchType: createEnum(dateComparisonOperators),
    matchDate: z.date(),
  });
const omsCustomAttributeDate1ConditionStepSchema: z.ZodType<OmsCustomAttributeDate1ConditionStep> =
  omsCustomAttributeDate1ConditionStepSchema_;
export interface OmsCustomAttributeDate1ConditionStep extends z.infer<
  typeof omsCustomAttributeDate1ConditionStepSchema_
> {}

const omsCustomAttributeDate2ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_DATE_2),
    matchType: createEnum(dateComparisonOperators),
    matchDate: z.date(),
  });
const omsCustomAttributeDate2ConditionStepSchema: z.ZodType<OmsCustomAttributeDate2ConditionStep> =
  omsCustomAttributeDate2ConditionStepSchema_;
export interface OmsCustomAttributeDate2ConditionStep extends z.infer<
  typeof omsCustomAttributeDate2ConditionStepSchema_
> {}

const omsCustomAttributeDate3ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_DATE_3),
    matchType: createEnum(dateComparisonOperators),
    matchDate: z.date(),
  });
const omsCustomAttributeDate3ConditionStepSchema: z.ZodType<OmsCustomAttributeDate3ConditionStep> =
  omsCustomAttributeDate3ConditionStepSchema_;
export interface OmsCustomAttributeDate3ConditionStep extends z.infer<
  typeof omsCustomAttributeDate3ConditionStepSchema_
> {}

const omsCustomerNameConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOMER_NAME),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsCustomerNameConditionStepSchema: z.ZodType<OmsCustomerNameConditionStep> =
  omsCustomerNameConditionStepSchema_;
export interface OmsCustomerNameConditionStep extends z.infer<
  typeof omsCustomerNameConditionStepSchema_
> {}

const omsCustomerOrderCountConditionStepSchema_ =
  baseOmsConditionStepSchema.extend({
    conditionType: z.literal(OmsConditionType.CUSTOMER_ORDER_COUNT),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  });
const omsCustomerOrderCountConditionStepSchema: z.ZodType<OmsCustomerOrderCountConditionStep> =
  omsCustomerOrderCountConditionStepSchema_;
export interface OmsCustomerOrderCountConditionStep extends z.infer<
  typeof omsCustomerOrderCountConditionStepSchema_
> {}

const omsCustomerTagConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOMER_TAG),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });
const omsCustomerTagConditionStepSchema: z.ZodType<OmsCustomerTagConditionStep> =
  omsCustomerTagConditionStepSchema_;
export interface OmsCustomerTagConditionStep extends z.infer<
  typeof omsCustomerTagConditionStepSchema_
> {}

const omsRecognizeSegmentConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.RECOGNIZE_SEGMENT),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
  });
const omsRecognizeSegmentConditionStepSchema: z.ZodType<OmsRecognizeSegmentConditionStep> =
  omsRecognizeSegmentConditionStepSchema_;
export interface OmsRecognizeSegmentConditionStep extends z.infer<
  typeof omsRecognizeSegmentConditionStepSchema_
> {}

const omsItemAmountConditionStepSchema_ = baseOmsConditionStepSchema.extend({
  conditionType: z.literal(OmsConditionType.ITEM_COST),
  matchType: createEnum(numberComparisonOperators),
  matchValue: z.number(),
});
const omsItemAmountConditionStepSchema: z.ZodType<OmsItemAmountConditionStep> =
  omsItemAmountConditionStepSchema_;
export interface OmsItemAmountConditionStep extends z.infer<
  typeof omsItemAmountConditionStepSchema_
> {}

const omsConditionStepSchema_ = z.union([
  omsOrderTagConditionStepSchema,
  omsSelectedShippingConditionStepSchema,
  omsSkuConditionStepSchema,
  omsProductNameConditionStepSchema,
  omsVariantTitleConditionStepSchema,
  omsItemQuantityConditionStepSchema,
  omsPackageWeightOzConditionStepSchema,
  omsTotalValueConditionStepSchema,
  omsTaxesPaidConditionStepSchema,
  omsDestinationCountryConditionStepSchema,
  omsDestinationProvinceConditionStepSchema,
  omsDeliveryMethodConditionStepSchema,
  omsStreetAddressConditionStepSchema,
  omsStreetAddressListInputConditionStepSchema,
  omsSalesChannelConditionStepSchema,
  omsProductTagConditionStepSchema,
  omsFulfillmentLocationConditionStepSchema,
  omsOriginStoreConditionStepSchema,
  omsOrderNotesConditionStepSchema,
  omsFraudRiskConditionStepSchema,
  omsAvailableInventoryConditionStepSchema,
  omsMarketplaceConditionStepSchema,
  omsShipmentTagConditionStepSchema,
  omsCustomString1ConditionStepSchema,
  omsCustomString2ConditionStepSchema,
  omsCustomString3ConditionStepSchema,
  omsCustomNumber1ConditionStepSchema,
  omsCustomNumber2ConditionStepSchema,
  omsCustomNumber3ConditionStepSchema,
  omsCustomDate1ConditionStepSchema,
  omsCustomDate2ConditionStepSchema,
  omsCustomDate3ConditionStepSchema,
  omsProductCustomString1ConditionStepSchema,
  omsProductCustomString2ConditionStepSchema,
  omsProductCustomString3ConditionStepSchema,
  omsProductCustomNumber1ConditionStepSchema,
  omsProductCustomNumber2ConditionStepSchema,
  omsProductCustomNumber3ConditionStepSchema,
  omsProductCustomDate1ConditionStepSchema,
  omsProductCustomDate2ConditionStepSchema,
  omsProductCustomDate3ConditionStepSchema,
  omsCustomAttributeString1ConditionStepSchema,
  omsCustomAttributeString2ConditionStepSchema,
  omsCustomAttributeString3ConditionStepSchema,
  omsCustomAttributeNumber1ConditionStepSchema,
  omsCustomAttributeNumber2ConditionStepSchema,
  omsCustomAttributeNumber3ConditionStepSchema,
  omsCustomAttributeDate1ConditionStepSchema,
  omsCustomAttributeDate2ConditionStepSchema,
  omsCustomAttributeDate3ConditionStepSchema,
  omsCustomerNameConditionStepSchema,
  omsCustomerOrderCountConditionStepSchema,
  omsCustomerTagConditionStepSchema,
  omsItemAmountConditionStepSchema,
  omsUspsZoneConditionStepSchema,
  omsLineItemCustomAttributeConditionStepSchema,
  omsAddressVerifiedConditionStepSchema,
  omsVendorConditionStepSchema,
  omsProductTypeConditionStepSchema,
  omsMarketplaceInternalNotesConditionStepSchema,
  omsPackageIsLearnedConditionStepSchema,
  omsDestinationForecastTemperatureConditionStepSchema,
  omsRecognizeSegmentConditionStepSchema,
]);
export const omsConditionStepSchema: z.ZodType<OmsConditionStep> =
  omsConditionStepSchema_;
export type OmsConditionStep = z.infer<typeof omsConditionStepSchema_>;

// TODO: Once zod 4 is released, switch back to discriminated union
const upsellFlowStepSchema_ = z.union([
  upsellConditionStepSchema,
  upsellStepSchema,
  downsellStepSchema,
  doNothingStepSchema,
  triggerStepSchema,
]);
export const upsellFlowStepSchema: z.ZodType<UpsellFlowStep> =
  upsellFlowStepSchema_;
export type UpsellFlowStep = z.infer<typeof upsellFlowStepSchema_>;

/**
 * Code step: invokes a merchant-authored artifact through the extensions
 * framework. The artifact row (extension_artifact) carries the input selector
 * that produced the artifact; the step only needs to point at the artifact.
 * Linear flow (single nextId) — the artifact returns actions to enqueue, not a
 * branching verdict.
 */
const omsCodeStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.OMS_CODE),
  name: z.string().optional(),
  artifactId: z.string(),
  nextId: z.string(),
});
export const omsCodeStepSchema: z.ZodType<OmsCodeStep> = omsCodeStepSchema_;
export interface OmsCodeStep extends z.infer<typeof omsCodeStepSchema_> {}

const omsStepSchema_ = z.union([
  omsConditionStepSchema,
  omsActionStepSchema,
  omsCodeStepSchema,
  doNothingStepSchema,
]);
export const omsStepSchema: z.ZodType<OmsStep> = omsStepSchema_;
export type OmsStep = z.infer<typeof omsStepSchema_>;

// A/B Test step schema
const abTestStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.AB_TEST),
  name: z.string().optional(),
  description: z.string().optional(),
  variants: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      weight: z.number().min(0).max(100),
      nextId: z.string(),
    }),
  ),
});
const abTestStepSchema: z.ZodType<AbTestStep> = abTestStepSchema_;
export interface AbTestStep extends z.infer<typeof abTestStepSchema_> {}

// TODO: Once zod 4 is released, switch back to discriminated union
const marketingStepSchema_ = z.union([
  triggerStepSchema,
  waitStepSchema,
  waitForEventStepSchema,
  sendEmailStepSchema,
  sendSmsStepSchema,
  sendWebhookStepSchema,
  manageCustomerTagsStepSchema,
  manageStaticSegmentStepSchema,
  conditionStepSchema,
  doNothingStepSchema,
  abTestStepSchema,
  recoverStepSchema,
  conciergeBlockStepSchema,
]);
export const marketingStepSchema: z.ZodType<MarketingStep> =
  marketingStepSchema_;
export type MarketingStep = z.infer<typeof marketingStepSchema_>;

const checkoutOptimizationStepSchema_ = z.union([
  rcoSplitStepSchema,
  checkoutExperienceStepSchema,
  rcoSegmentStepSchema,
  blockCheckoutStepSchema,
  checkoutOptimizationTriggerStepSchema,
]);
export const checkoutOptimizationStepSchema: z.ZodType<CheckoutOptimizationStep> =
  checkoutOptimizationStepSchema_;
export type CheckoutOptimizationStep = z.infer<
  typeof checkoutOptimizationStepSchema_
>;

export const checkoutOptimizationStepArraySchema: z.ZodType<
  CheckoutOptimizationStep[]
> = z.array(checkoutOptimizationStepSchema);

const checkoutParcelStepSchema_ = z.union([
  rcoCheckoutPackageStepSchema,
  rcoCheckoutPackageConditionStepSchema,
]);
export const checkoutParcelStepSchema: z.ZodType<CheckoutParcelStep> =
  checkoutParcelStepSchema_;
export type CheckoutParcelStep = z.infer<typeof checkoutParcelStepSchema_>;

const checkoutParcelFlowStepSchema_ = z.union([
  checkoutParcelStepSchema,
  checkoutPackageTriggerStepSchema,
]);
const checkoutParcelFlowStepSchema: z.ZodType<CheckoutParcelFlowStep> =
  checkoutParcelFlowStepSchema_;
export type CheckoutParcelFlowStep = z.infer<
  typeof checkoutParcelFlowStepSchema_
>;

export const checkoutParcelFlowStepArraySchema: z.ZodType<
  CheckoutParcelFlowStep[]
> = z.array(checkoutParcelFlowStepSchema);
export type CheckoutParcelFlowStepArray = CheckoutParcelFlowStep[];

const postPurchaseFlowStepSchema_ = z.union([
  postPurchasePageStepSchema,
  postPurchaseSegmentStepSchema,
  postPurchaseSplitStepSchema,
  doNothingStepSchema,
  postPurchaseTriggerStepSchema,
]);
export const postPurchaseFlowStepSchema: z.ZodType<PostPurchaseFlowStep> =
  postPurchaseFlowStepSchema_;
export type PostPurchaseFlowStep = z.infer<typeof postPurchaseFlowStepSchema_>;

export const postPurchaseFlowStepArraySchema: z.ZodType<
  PostPurchaseFlowStep[]
> = z.array(postPurchaseFlowStepSchema);
export type PostPurchaseFlowStepArray = PostPurchaseFlowStep[];

const trackingPageStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.TRACKING_PAGE),
  trackingPageId: z.string(),
  nextId: z.string().optional(),
  // Scheduled promotions: each event serves its own tracking page instead of
  // `trackingPageId` while its window is open. See
  // tracking-page-scheduled-event.ts for the runtime selection helpers.
  scheduledEvents: z.array(trackingPageScheduledEventSchema).optional(),
});
const trackingPageStepSchema: z.ZodType<TrackingPageStep> =
  trackingPageStepSchema_;
export interface TrackingPageStep extends z.infer<
  typeof trackingPageStepSchema_
> {}

const trackingPageFlowStepSchema_ = z.union([
  trackingPageTriggerStepSchema,
  trackingPageStepSchema,
  conditionStepSchema,
  doNothingStepSchema,
  abTestStepSchema,
]);
export const trackingPageFlowStepSchema: z.ZodType<TrackingPageFlowStep> =
  trackingPageFlowStepSchema_;
export type TrackingPageFlowStep = z.infer<typeof trackingPageFlowStepSchema_>;

export const trackingPageFlowStepArraySchema: z.ZodType<
  TrackingPageFlowStep[]
> = z.array(trackingPageFlowStepSchema);
export type TrackingPageFlowStepArray = TrackingPageFlowStep[];

const rulesFlowStepSchema_ = z.union([
  ruleConditionsStepSchema,
  ruleActionsStepSchema,
  waitStepSchema,
]);
export const rulesFlowStepSchema: z.ZodType<RulesFlowStep> =
  rulesFlowStepSchema_;
export type RulesFlowStep = z.infer<typeof rulesFlowStepSchema_>;

export const rulesFlowStepArraySchema: z.ZodType<RulesFlowStep[]> =
  z.array(rulesFlowStepSchema);
export type RulesFlowStepArray = RulesFlowStep[];

const rulesV3FlowStepSchema_ = z.union([
  ruleV3ConditionsStepSchema,
  ruleV3ActionsStepSchema,
  waitStepSchema,
]);
export const rulesV3FlowStepSchema: z.ZodType<RulesV3FlowStep> =
  rulesV3FlowStepSchema_;
export type RulesV3FlowStep = z.infer<typeof rulesV3FlowStepSchema_>;

export const rulesV3FlowStepArraySchema: z.ZodType<RulesV3FlowStep[]> = z.array(
  rulesV3FlowStepSchema,
);
export type RulesV3FlowStepArray = RulesV3FlowStep[];

const baseWorkOrderConditionStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.WORK_ORDER_CONDITION),
  conditionType: createEnum(OmsConditionType),
  nextTrueId: z.string(),
  nextFalseId: z.string(),
});

const baseWorkOrderConditionStepSchemaWithWhere =
  baseWorkOrderConditionStepSchema.extend({
    where: z
      .array(
        baseWorkOrderConditionStepSchema.omit({
          id: true,
          type: true,
          nextTrueId: true,
          nextFalseId: true,
        }),
      )
      .optional(),
  });

const workOrderConditionStepSchema_ = z.union([
  baseWorkOrderConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.SKU),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  }),
  baseWorkOrderConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_NAME),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  }),
  baseWorkOrderConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.VARIANT_TITLE),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  }),
  baseWorkOrderConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_TYPE),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  }),
  baseWorkOrderConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_TAG),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  }),
  baseWorkOrderConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.ITEM_QUANTITY),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  }),
  baseWorkOrderConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.VENDOR),
    matchType: createEnum(ListToListMatchType),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  }),
  baseWorkOrderConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.ITEM_COST),
    matchType: createEnum(numberComparisonOperators),
    matchValue: z.number(),
  }),
  baseWorkOrderConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.LINE_ITEM_CUSTOM_ATTRIBUTE),
    matchKey: z.string(),
    matchType: createEnum(StringToListMatchType),
    matchValues: z.array(z.string()),
  }),
]);

export const workOrderConditionStepSchema: z.ZodType<WorkOrderConditionFlowStep> =
  workOrderConditionStepSchema_ as z.ZodType<WorkOrderConditionFlowStep>;
export type WorkOrderConditionFlowStep = {} & z.infer<
  typeof workOrderConditionStepSchema_
>;

const workOrderWorkStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.WORK_ORDER_WORK_STEP),
  title: z.string().optional(),
  nextId: z.string().nullish(),
  instructions: z.string().optional(),
  displayAttributes: z
    .array(z.object({ label: z.string(), attributeKey: z.string() }))
    .optional(),
  inputs: z
    .array(
      z.object({
        kind: z.union([z.literal("product"), z.literal("bom")]),
        id: z.string(),
        quantity: z.number().int(),
      }),
    )
    .optional(),
  outputs: z
    .array(
      z.object({
        kind: z.union([z.literal("product"), z.literal("bom")]),
        id: z.string(),
        quantity: z.number().int(),
      }),
    )
    .optional(),
  stationId: z.string().nullish(),
  printBarcodeOnComplete: z.boolean().optional(),
  showSiblingBatches: z.boolean().optional(),
  autoReturnToQueueOnHandoff: z.boolean().optional(),
});
export const workOrderWorkStepSchema: z.ZodType<WorkOrderWorkStepFlowStep> =
  workOrderWorkStepSchema_;
export interface WorkOrderWorkStepFlowStep extends z.infer<
  typeof workOrderWorkStepSchema_
> {}

const workOrderFlowStepSchema_ = z.union([
  workOrderWorkStepSchema,
  workOrderConditionStepSchema,
]);
export const workOrderFlowStepSchema: z.ZodType<WorkOrderFlowStep_> =
  workOrderFlowStepSchema_;
export type WorkOrderFlowStep_ = z.infer<typeof workOrderFlowStepSchema_>;

const processReturnStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.PROCESS_RETURN),
  nextId: z.string().nullish(),
  sendNotifications: z.boolean().nullish(),
});

export const processReturnStepSchema: z.ZodType<ProcessReturnStep> =
  processReturnStepSchema_;
export interface ProcessReturnStep extends z.infer<
  typeof processReturnStepSchema_
> {}

const returnProcessingFlowStepSchema_ = z.union([
  triggerStepSchema,
  waitStepSchema,
  conditionStepSchema,
  doNothingStepSchema,
  processReturnStepSchema,
  compensationStepSchema,
  restockingStepSchema,
  returnFlowConditionStepSchema,
]);
export const returnProcessingFlowStepSchema: z.ZodType<ReturnProcessingFlowStep> =
  returnProcessingFlowStepSchema_;
export type ReturnProcessingFlowStep = z.infer<
  typeof returnProcessingFlowStepSchema_
>;

const stepSchema_ = z.union([
  omsStepSchema,
  marketingStepSchema,
  upsellFlowStepSchema,
  checkoutOptimizationStepSchema,
  checkoutParcelStepSchema,
  postPurchaseFlowStepSchema,
  abTestStepSchema,
  trackingPageFlowStepSchema,
  rulesFlowStepSchema,
  rulesV3FlowStepSchema,
  workOrderFlowStepSchema,
  returnProcessingFlowStepSchema,
]);
export const stepSchema: z.ZodType<FlowStep> = stepSchema_;
export type FlowStep = {} & z.infer<typeof stepSchema_>;

/** Union of the four step types valid in a rules-v3 flow. */
export const rulesV3StepSchema = z.union([
  rulesTriggerStepSchema,
  ruleV3ConditionsStepSchema,
  ruleV3ActionsStepSchema,
  waitStepSchema,
]);
export type RulesV3Step = z.infer<typeof rulesV3StepSchema>;

export const advancedFlowSchema_ = z.object({
  // Optional: global Withdrawal flows are team-less. All other categories
  // carry a team. Consumers that read `flow.team` must handle undefined.
  team: zExt.objectId().optional(),
  name: z.string(),
  description: z.string().optional(),
  enabled: z.boolean(),
  steps: z
    .array(stepSchema)
    .refine(
      (steps) => steps.filter((s) => s.type === StepType.TRIGGER).length === 1,
      "Flow must have exactly one trigger step",
    ),
  schemaType: createEnum(SchemaType),
  category: createEnum(categories),
  createdByUserId: zExt.objectId().optional(),
  createdAt: z.date().optional(),
  index: z.number().optional(),
  versionGroupId: zExt.objectId(),
  flowRevisionId: zExt.objectId().optional(),
  // Drafts can have publishedAt explicitly null in Mongo (set by
  // createNewDraftRevision). Accept both null and undefined; consumers
  // already treat both as "not published" via `!doc.publishedAt`.
  publishedAt: z.date().nullish(),
  metadata: z.string().optional(),
  _id: zExt.objectId(),
  updatedAt: z.date().optional(),
  __v: z.number().optional(),
});
// Backwards compatibility: normalize deprecated "sms_marketing_confirmed" to "sms_marketing_signup"
export const advancedFlowSchema: z.ZodType<AdvancedFlow> = z.preprocess(
  (val: any) => {
    if (!val || typeof val !== "object") {
      return val;
    }

    // Convert Mongoose document to plain object if needed
    const plainObj = val.toObject ? val.toObject() : val;

    // Normalize flow-level schemaType
    if (plainObj.schemaType === "sms_marketing_confirmed") {
      plainObj.schemaType = "sms_marketing_signup";
    }

    // Normalize trigger step schemaType
    if (Array.isArray(plainObj.steps)) {
      plainObj.steps = plainObj.steps.map((step: any) => {
        if (
          step &&
          step.type === "trigger" &&
          step.schemaType === "sms_marketing_confirmed"
        ) {
          return { ...step, schemaType: "sms_marketing_signup" };
        }
        return step;
      });
    }

    return plainObj;
  },
  advancedFlowSchema_,
) as z.ZodType<AdvancedFlow>;
// TODO: Switch to cleaner version in mid-December 2025 after migration is complete
// Replace above with:
// export const advancedFlowSchema: z.ZodType<AdvancedFlow> = advancedFlowSchema_;
export interface AdvancedFlow extends z.infer<typeof advancedFlowSchema_> {}

export type NewAdvancedFlow = Omit<
  AdvancedFlow,
  "createdAt" | "updatedAt" | "schemaVersion" | "_id" | "versionGroupId"
>;

// For every trigger key, there should be a trigger step schema
// in this file. This function expression enforces that.
// MarketingAgentCustomKey is coordinator-wake only (not a flow TriggerStep).
// eslint-disable-next-line no-unused-expressions
() => {
  const a = "" as unknown as XorOfUnions<
    TriggerStep["key"],
    Exclude<TriggerKey, MarketingAgentCustomKey>
  >;
  a satisfies never;
};
